import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import api from '../api';
import { HistoryLink } from '../components/LastChanged';
import { useToast } from '../context/ToastContext';
import CapacityCheckModal from '../components/CapacityCheckModal';
import { CompletePlanModal } from './Schedule';
import StockModal from '../components/StockModal';
import GapModal from '../components/GapModal';
import { openInvoicePdf } from '../invoicePdf';

const TEAL   = '#14b8a6';
const BG     = '#0f1117';
const CARD   = '#1e2329';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#f0f2f5';

function isoWeek(date) {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - ((d.getDay()+6) % 7));
  const w1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay()+6) % 7)) / 7);
}

function currentYear() { return new Date().getFullYear(); }
function currentWeek() { return isoWeek(new Date()); }

// ─── Smart Excel layout detection ────────────────────────────────────────────

const PART_NUM_KW  = ['part number','part no','part nr','part#','product number','product no','product nr','article number','article no','item number','item no','matnr','mat.-nr','mat.nr','mat-nr','art.nr','artnr','teilenummer','artikelnummer','code','ref','sku'];
const PART_NAME_KW = ['part name','product name','article name','item name','description','name','bezeichnung'];
const QTY_KW       = ['quantity','qty','menge','amount','pieces','pcs','anzahl','stk'];
const DATE_KW      = ['deadline','delivery week','delivery date','due date','delivery','due','liefertermin','lieferwoche'];

function normH(h) { return String(h == null ? '' : h).trim().toLowerCase(); }


// Convert any week-like value (number, "Wk 30", "KW30", date string, Excel serial) → ISO week int or null
function toWeekNum(val) {
  if (val == null) return null;
  const s = String(val).trim();
  // Pure 1–53
  if (/^\d{1,2}$/.test(s)) { const n = parseInt(s); if (n >= 1 && n <= 53) return n; }
  // "Wk 30", "KW30", "CW 30", "Week 30"
  const m = s.match(/^(?:wk|kw|cw|w|week|woche)[\s\-_]*(\d{1,2})$/i);
  if (m) { const n = parseInt(m[1]); if (n >= 1 && n <= 53) return n; }
  // Excel date serial (40000–50000 ≈ 2009–2036)
  const raw = typeof val === 'number' ? val : parseFloat(s);
  if (!isNaN(raw) && raw > 40000 && raw < 50000)
    return isoWeek(new Date((raw - 25569) * 86400000));
  // Date strings: ISO, DD.MM.YYYY, DD/MM/YYYY, MM/DD/YYYY
  if (s.length >= 8) {
    let d = new Date(s);
    if (isNaN(d)) {
      const p = s.split(/[./-]/);
      if (p.length === 3) {
        // Try DD.MM.YYYY first, fall back to MM/DD/YYYY
        d = new Date(`${p[2]}-${p[1].padStart(2,'0')}-${p[0].padStart(2,'0')}`);
        if (isNaN(d)) d = new Date(`${p[2]}-${p[0].padStart(2,'0')}-${p[1].padStart(2,'0')}`);
      }
    }
    if (!isNaN(d)) return isoWeek(d);
  }
  return null;
}

function detectLayout(header) {
  let partNumCol = -1, partNameCol = -1, qtyCol = -1, dateCol = -1;
  const weekCols = []; // { colIndex, weekNum }

  for (let i = 0; i < header.length; i++) {
    const h = normH(header[i]);
    if (!h) continue;

    if (partNumCol  === -1 && PART_NUM_KW.some(k  => h === k || h.startsWith(k))) { partNumCol  = i; continue; }
    if (partNameCol === -1 && PART_NAME_KW.some(k => h === k || h.includes(k)))    { partNameCol = i; continue; }
    if (qtyCol      === -1 && QTY_KW.some(k       => h === k || h.includes(k)))    { qtyCol      = i; continue; }
    if (dateCol     === -1 && DATE_KW.some(k       => h === k || h.includes(k)))    { dateCol     = i; continue; }

    const wn = toWeekNum(header[i]);
    if (wn !== null) weekCols.push({ colIndex: i, weekNum: wn });
  }

  if (partNumCol === -1) partNumCol = 0; // fallback: first column

  // Wide = multiple week columns as headers; Tall = one date/week column + qty column (or inline in cells)
  const format = weekCols.length >= 2 ? 'wide' : 'tall';
  return { partNumCol, partNameCol, qtyCol, dateCol, weekCols, format };
}

// ─── sub-components ──────────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Shared: compute Monday and Friday of ISO week. Returns null when week/year missing.
function weekBounds(week, year) {
  if (!week || !year) return null;
  const jan4 = new Date(year, 0, 4);
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (week - 1) * 7);
  const fri = new Date(mon); fri.setDate(mon.getDate() + 4);
  return { mon, fri };
}

function formatDeadline(week, year, deliveryDate) {
  const fmt = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (deliveryDate) {
    const d = new Date(deliveryDate + 'T12:00:00Z');
    return `Wk ${week} · ${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${year}`;
  }
  const b = weekBounds(week, year);
  if (!b) return week ? `Wk ${week}` : '—';
  return `Wk ${week} · ${fmt(b.mon)}–${fmt(b.fri)} ${year}`;
}

// Days a line has on the board: its own runs, and a batch it is part of.
function daysOnBoard(l) {
  return (l.board_days || 0) + (l.batched_days || 0);
}

// A line wants planning when nothing of it is still planned on the board (its
// own runs or a batch) and parts are still missing: ordered, less made, less
// from stock -- whatever its old "scheduled" mark says. Undoing or taking off
// an order's runs left the mark set, so the order could not be scheduled
// again; the planner then plans only what is still missing (11 Oct 2026).
function needsPlan(l) {
  if (l.board_days == null) return !l.scheduled;   // a server from before 2 Oct
  if ((l.batched_days || 0) > 0) return false;
  if ((l.board_days || 0) - (l.done_days || 0) > 0) return false;
  return (l.quantity || 0) - (l.current_qty || 0) - (l.from_stock_qty || 0) > 0;
}

// `solid` for a state that is done (Completed), so it reads apart from the
// outlined ones still in progress.
function Badge({ children, colour, solid = false }) {
  return (
    <span style={{ display: 'inline-block', background: solid ? colour : colour + '22', color: solid ? '#052e16' : colour, fontSize: '9px',
      fontWeight: '700', borderRadius: '6px', border: `1px solid ${colour}66`, whiteSpace: 'nowrap',
      padding: '3px' }}>
      {children}
    </span>
  );
}

// How an order with no delivery week should be scheduled. Only shown when there is
// no date — when a date exists it governs and the choice is irrelevant.
const SCHEDULE_INTENTS = [
  { value: 'asap',   label: 'As soon as possible', colour: '#14b8a6',
    hint: 'Wanted soon, date not confirmed. Scheduled early, but after work that has a delivery date.' },
  { value: 'filler', label: 'Filler / buffer',     colour: '#a78bfa',
    hint: 'Run it when there is a gap. Never takes a slot that dated work wants.' },
  { value: 'hold',   label: 'Hold — needs a date', colour: '#f59e0b',
    hint: 'Not scheduled at all. Appears in proposals as needing a delivery week.' },
];

function ScheduleIntentBadge({ order }) {
  if (order?.delivery_week || order?.delivery_date) return null;
  const intent = SCHEDULE_INTENTS.find(i => i.value === (order?.schedule_intent || 'asap'));
  if (!intent) return null;
  return <Badge colour={intent.colour}>{intent.value === 'hold' ? 'On hold' : intent.label}</Badge>;
}

function ScheduleIntentField({ value, onChange }) {
  const current = SCHEDULE_INTENTS.find(i => i.value === value) || SCHEDULE_INTENTS[0];
  return (
    <div style={{ background: '#0f1117', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '10px 12px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, marginBottom: '7px' }}>
        No delivery week — how should this be scheduled?
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {SCHEDULE_INTENTS.map(i => {
          const on = i.value === value;
          return (
            <button
              key={i.value}
              type="button"
              onClick={() => onChange(i.value)}
              style={{
                flex: '1 1 130px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                background: on ? i.colour + '1f' : 'transparent',
                border: `1px solid ${on ? i.colour + '99' : BORDER}`,
                borderRadius: '7px', padding: '7px 9px',
                color: on ? i.colour : MUTED, fontSize: '12px', fontWeight: '700',
                transition: 'background 0.12s, border-color 0.12s, color 0.12s',
              }}
            >
              {i.label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: '11px', color: MUTED, marginTop: '7px', lineHeight: 1.45 }}>
        {current.hint}
      </div>
    </div>
  );
}

function DeadlineCell({ week, year, deliveryDate }) {
  if (!week) return <span style={{ color: MUTED }}>—</span>;
  const fmt = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const b = weekBounds(week, year);
  const sub = deliveryDate ? fmtDate(deliveryDate) : b ? `${fmt(b.mon)}–${fmt(b.fri)}` : null;
  return (
    <div style={{ lineHeight: 1.25 }}>
      <div style={{ color: TEAL, fontWeight: '700', fontSize: '11px' }}>Wk {week}</div>
      {sub && <div style={{ color: MUTED, fontSize: '10px', marginTop: '1px' }}>{sub}</div>}
    </div>
  );
}

// ─── Post-import order naming screen ─────────────────────────────────────────

function NamingScreen({ importedOrders, onContinue, onSkip, onCancelImport }) {
  const [names, setNames] = useState(
    Object.fromEntries(
      importedOrders.map(o => [o.id, { customer: o.customer || '', order_number: o.order_number || '' }])
    )
  );

  function set(id, field, val) {
    setNames(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  }

  const inp = {
    background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '6px',
    color: TEXT, padding: '5px 10px', fontSize: '13px', outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px',
        maxWidth: '560px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>

        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>Name your orders</div>
          <div style={{ fontSize: '12px', color: MUTED, marginTop: '4px' }}>
            {importedOrders.length} order{importedOrders.length !== 1 ? 's' : ''} created — give each a name or skip.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,auto) 1fr 1fr', gap: '8px', alignItems: 'center' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Week</div>
            <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Customer</div>
            <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Order #</div>
            {importedOrders.map(o => (
              <React.Fragment key={o.id}>
                <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '700', whiteSpace: 'nowrap', paddingRight: '8px' }}>
                  Wk {o.delivery_week}/{o.delivery_year}
                </div>
                <input
                  value={names[o.id].customer}
                  onChange={e => set(o.id, 'customer', e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && onContinue(names)}
                  placeholder="Customer"
                  style={inp}
                />
                <input
                  value={names[o.id].order_number}
                  onChange={e => set(o.id, 'order_number', e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && onContinue(names)}
                  placeholder="Reference"
                  style={inp}
                />
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          {onCancelImport && (
            <button onClick={onCancelImport}
              style={{ background: 'none', border: '1px solid #ef444466', color: '#f87171',
                borderRadius: '8px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', marginRight: 'auto' }}>
              <i className="fa-solid fa-trash" style={{ marginRight: '6px' }} />Cancel Import
            </button>
          )}
          <button onClick={onSkip}
            style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED,
              borderRadius: '8px', padding: '8px 20px', fontSize: '13px', cursor: 'pointer' }}>
            Skip
          </button>
          <button onClick={() => onContinue(names)}
            style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px',
              padding: '8px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            Continue <i className="fa-solid fa-arrow-right" style={{ marginLeft: '6px' }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function BatchOpportunitiesScreen({ opportunities, onContinue, onCancel }) {
  const [decisions, setDecisions] = useState({}); // { [part_id]: true | false }

  function toggle(partId) {
    setDecisions(prev => ({ ...prev, [partId]: !prev[partId] }));
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px',
        maxWidth: '640px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>

        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>Batch Opportunities</div>
          <div style={{ fontSize: '12px', color: MUTED, marginTop: '4px' }}>
            These parts appear in multiple orders. Tick to produce in one run.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {opportunities.map(opp => {
            const checked = !!decisions[opp.part_id];
            return (
              <label key={opp.part_id}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0',
                  borderBottom: `1px solid ${BORDER}22`, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(opp.part_id)}
                  style={{ accentColor: TEAL, width: '15px', height: '15px', marginTop: '2px', flexShrink: 0, cursor: 'pointer' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '700', fontSize: '13px' }}>{opp.part_name || `Part #${opp.part_id}`}</span>
                    {opp.part_number && (
                      <span style={{ fontFamily: 'monospace', fontSize: '11px', color: TEAL }}>{opp.part_number}</span>
                    )}
                    <span style={{ fontSize: '10px', background: '#f59e0b22', color: '#fbbf24',
                      border: '1px solid #f59e0b44', borderRadius: '4px', padding: '1px 6px' }}>
                      {opp.reason}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {(opp.groups || []).map((g, i) => (
                      <div key={i} style={{ fontSize: '11px', color: MUTED,
                        background: '#161b22', borderRadius: '6px', padding: '4px 8px' }}>
                        <span style={{ color: TEXT, fontWeight: '600' }}>{g.quantity?.toLocaleString()} pcs</span>
                        {' · '}Wk {g.delivery_week}/{g.delivery_year}
                      </div>
                    ))}
                  </div>
                  {checked && (
                    <div style={{ fontSize: '11px', color: TEAL, marginTop: '5px' }}>
                      <i className="fa-solid fa-circle-check" style={{ marginRight: '5px' }} />
                      Will be produced in one run · {(opp.groups || []).reduce((s, g) => s + (g.quantity || 0), 0).toLocaleString()} pcs total
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ background: 'none', border: `1px solid ${BORDER}`,
            color: MUTED, borderRadius: '8px', padding: '8px 20px', fontSize: '13px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={() => onContinue(decisions)}
            style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px',
              padding: '8px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            Continue <i className="fa-solid fa-arrow-right" style={{ marginLeft: '6px' }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ConflictModal({ conflicts, warnings, proposed, onAccept, onReject, onRepropose, batchOpportunities = [], onBatchDecide, onCancelImport, onConfirmNoSchedule, floorCycles = [], buildOrder = [], colourNotes = [] }) {
  const [forcePrimary, setForcePrimary] = useState(new Set());
  const navigate = useNavigate();
  // Parts the floor runs off their cycle time (utils/floorCycle): which ones
  // this plan uses the floor's figure for. Starts as the plan was made.
  const floorBy = Object.fromEntries(floorCycles.map(f => [f.part_id, f]));
  const [floorUse, setFloorUse] = useState(() => new Set(floorCycles.filter(f => f.using === 'floor').map(f => f.part_id)));
  const floorChanged = floorCycles.some(f => (f.using === 'floor') !== floorUse.has(f.part_id));
  // A new proposal says which figure it used: the switches follow it.
  const floorKey = floorCycles.map(f => `${f.part_id}:${f.using}`).join(',');
  useEffect(() => {
    setFloorUse(new Set(floorCycles.filter(f => f.using === 'floor').map(f => f.part_id)));
  }, [floorKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [bufferOverrides, setBufferOverrides] = useState({});
  const [tooltip, setTooltip] = useState(null); // { msgs, level, x, y }
  const [partLinkOpened, setPartLinkOpened] = useState(false);
  const [showRerunBanner, setShowRerunBanner] = useState(false);
  const [summary, setSummary] = useState(null);       // { summary, source }
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Re-fetch only when the proposal itself changes (a re-propose), not on every
  // parent render — the array props are rebuilt but describe the same plan.
  const proposalKey = `${proposed.length}|${warnings.length}|${proposed[0]?.scheduled_date || ''}|${proposed[proposed.length - 1]?.scheduled_date || ''}`;

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    api.post('/api/v1/orders/schedule/explain', {
      proposed, warnings, batch_opportunities: batchOpportunities,
    })
      .then(r => { if (!cancelled) setSummary(r.data); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [proposalKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!partLinkOpened) return;
    function handleVisibility() {
      if (document.visibilityState === 'visible') setShowRerunBanner(true);
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [partLinkOpened]);

  function openPartLink(partId) {
    setPartLinkOpened(true);
    window.open(`/admin/parts/${partId}`, '_blank');
  }

  function handleRerun() {
    setShowRerunBanner(false);
    onRepropose([...forcePrimary], bufferOverrides, [...floorUse]);
  }
  const [showingBatch, setShowingBatch] = useState(batchOpportunities.length > 0);

  // part IDs where buffer is the only obstacle (could fit without it)
  const bufferConflictPartIds = new Set(warnings.filter(w => w.buffer_conflict && w.part_id).map(w => w.part_id));

  // Build alert map: part_id → { level: 'conflict'|'warning', msgs: string[] }
  const alertMap = {};
  for (const w of warnings) {
    if (!w.part_id) continue;
    if (!alertMap[w.part_id]) alertMap[w.part_id] = { level: 'warning', msgs: [] };
    const note = w.buffer_conflict ? `${w.reason} — set a shorter buffer to re-schedule` : w.reason;
    alertMap[w.part_id].msgs.push(note);
  }
  for (const c of conflicts) {
    // conflicts don't carry part_id — match by name
    const matched = proposed.find(p => p.part_name === c.part);
    const key = matched?.part_id || c.part;
    if (!alertMap[key]) alertMap[key] = { level: 'conflict', msgs: [] };
    else alertMap[key].level = 'conflict';
    alertMap[key].msgs.push(`${c.reason} on ${c.date}`);
  }

  // Build per-part summary: part_id → { part_number, part_name, totalQty, finishDate, hasAlt }
  const partSummary = {};
  for (const p of proposed) {
    if (!partSummary[p.part_id]) {
      partSummary[p.part_id] = { part_id: p.part_id, part_number: p.part_number, part_name: p.part_name, totalQty: 0, finishDate: p.scheduled_date, hasAlt: false };
    }
    partSummary[p.part_id].totalQty += p.target_qty || 0;
    if (p.scheduled_date > partSummary[p.part_id].finishDate) partSummary[p.part_id].finishDate = p.scheduled_date;
    if (p.is_alternative_machine) partSummary[p.part_id].hasAlt = true;
  }
  // Add any warned/conflicted parts that produced zero scheduled entries
  for (const w of warnings) {
    if (w.part_id && !partSummary[w.part_id]) {
      partSummary[w.part_id] = { part_id: w.part_id, part_number: null, part_name: w.part, totalQty: 0, finishDate: null, hasAlt: false };
    }
  }
  const summaryRows = Object.values(partSummary);

  // What the planner could not place goes on the backlog beside the board,
  // with its reason (20 Oct 2026): its lines, and the way there. With
  // something placed, the board opens in a new tab, so this proposal is not
  // lost before it is confirmed.
  const placedLines = new Set(proposed.flatMap(p => [p.order_line_id, ...(Array.isArray(p.order_line_ids) ? p.order_line_ids : [])]).filter(Boolean).map(Number));
  const backlogLines = [...new Set(warnings.filter(w => w.line_id && !w.is_warning_only && !placedLines.has(Number(w.line_id))).map(w => Number(w.line_id)))];
  const backlogParts = new Set(warnings.filter(w => w.line_id && backlogLines.includes(Number(w.line_id))).map(w => w.part_id));
  const toBacklog = (lineIds = backlogLines) => {
    const url = `/admin/schedule?backlog=1&lines=${lineIds.join(',')}`;
    if (proposed.length > 0) window.open(url, '_blank'); else navigate(url);
  };

  function toggleForcePrimary(partId) {
    setForcePrimary(prev => {
      const next = new Set(prev);
      next.has(partId) ? next.delete(partId) : next.add(partId);
      return next;
    });
  }

  function finishLabel(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const week = isoWeek(d);
    return `Wk ${week} · ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }

  if (showingBatch && batchOpportunities.length > 0) {
    return (
      <BatchOpportunitiesScreen
        opportunities={batchOpportunities}
        onContinue={decisions => { setShowingBatch(false); onBatchDecide(decisions); }}
        onCancel={onReject}
      />
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px',
        maxWidth: '760px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>Review Proposed Schedule</div>
          <div style={{ fontSize: '12px', color: MUTED, marginTop: '4px' }}>
            {proposed.length} job entries · {summaryRows.length} parts · {new Set(proposed.map(p => p.scheduled_date)).size} days
            {Object.keys(alertMap).length > 0 && (
              <span style={{ marginLeft: '10px', color: Object.values(alertMap).some(a => a.level === 'conflict') ? '#f87171' : '#fbbf24' }}>
                · {Object.keys(alertMap).length} issue{Object.keys(alertMap).length !== 1 ? 's' : ''} flagged
              </span>
            )}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {backlogLines.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', background: 'rgba(20,184,166,0.06)', border: `1px solid ${TEAL}55`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px' }}>
              <i className="fa-solid fa-inbox" style={{ color: TEAL }} />
              <span style={{ flex: 1, minWidth: '240px', color: '#d1d5db' }}>
                <b style={{ color: TEXT }}>{backlogParts.size} part{backlogParts.size === 1 ? '' : 's'} could not be placed</b> and {backlogParts.size === 1 ? 'is' : 'are'} on the backlog beside the Schedule board, with why. Make room there and drag {backlogParts.size === 1 ? 'it' : 'them'} on as you see fit{proposed.length > 0 ? ' — confirm the rest here first' : ''}.
              </span>
              <button onClick={() => toBacklog()} style={{ background: 'none', border: `1px solid ${TEAL}88`, color: TEAL, borderRadius: '8px', padding: '6px 14px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Open the backlog{proposed.length > 0 ? ' ↗' : ' →'}
              </button>
            </div>
          )}
          {/* Read-this-first summary. The engine works all of this out while
              proposing and then discards it; this is the only place it surfaces
              as something you can read in one pass. */}
          {(summaryLoading || summary) && (
            <div style={{
              background: 'rgba(20,184,166,0.06)', border: '1px solid rgba(20,184,166,0.28)',
              borderLeft: '3px solid #14b8a6', borderRadius: '8px',
              padding: '12px 14px', marginBottom: '12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
                <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#14b8a6', fontSize: '11px' }} />
                <span style={{ fontSize: '10px', fontWeight: '800', letterSpacing: '0.07em', textTransform: 'uppercase', color: '#14b8a6' }}>
                  What this proposal does
                </span>
                {summary?.source === 'computed' && (
                  <span title={summary.reason || 'Written without the language model'}
                    style={{ fontSize: '9px', fontWeight: '700', color: MUTED, border: `1px solid ${BORDER}`, borderRadius: '3px', padding: '1px 5px' }}>
                    offline
                  </span>
                )}
              </div>
              {summaryLoading ? (
                <div style={{ fontSize: '12px', color: MUTED, fontStyle: 'italic' }}>Reading the plan…</div>
              ) : (
                <div style={{ fontSize: '12.5px', color: TEXT, lineHeight: 1.55 }}>{summary.summary}</div>
              )}
            </div>
          )}

          {/* Notices about the proposal as a whole rather than a single part. The
              per-part alert map keys on part_id, so these would otherwise vanish. */}
          {warnings.filter(w => w.proposal_level).map((w, i) => (
            <div key={i} style={{ display: 'flex', gap: '9px', alignItems: 'flex-start',
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ color: '#f59e0b', fontSize: '12px', marginTop: '2px', flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: '#fbbf24', lineHeight: 1.5 }}>{w.reason}</span>
            </div>
          ))}

          {summaryRows.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Summary by Part
                  {Object.keys(alertMap).length > 0 && (
                    <span style={{ marginLeft: '8px', fontSize: '10px', color: '#f59e0b', fontWeight: '400', textTransform: 'none' }}>
                      — hover <i className="fa-solid fa-triangle-exclamation" /> for details
                    </span>
                  )}
                </div>
                {(forcePrimary.size > 0 || Object.keys(bufferOverrides).length > 0 || floorChanged) && (
                  <button onClick={() => onRepropose([...forcePrimary], bufferOverrides, [...floorUse])}
                    style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '6px',
                      padding: '5px 14px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                    <i className="fa-solid fa-rotate" style={{ marginRight: '6px' }} />Re-run with overrides
                  </button>
                )}
              </div>
              {/* Colour order: what decided the order on each machine -- the colour, or the date (utils/colourOrder). */}
              {colourNotes.length > 0 && (
                <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${BORDER}`, background: '#161b22', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {colourNotes.map((n, i) => (
                    <div key={i} style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.5, display: 'flex', gap: '8px' }}>
                      <i className={`fa-solid ${n.used === 'date' ? 'fa-calendar-day' : n.used === 'purge' ? 'fa-fill-drip' : 'fa-palette'}`} style={{ color: n.used === 'date' ? '#f59e0b' : n.used === 'purge' ? '#a78bfa' : TEAL, marginTop: '3px' }} />
                      <span>{n.says}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Product build order: what decided the order of a product's parts (utils/buildOrder). */}
              {buildOrder.some(b => b.note) && (
                <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${BORDER}`, background: '#161b22', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {buildOrder.filter(b => b.note).map(b => (
                    <div key={b.key} style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.5, display: 'flex', gap: '8px' }}>
                      <i className={`fa-solid ${b.note.used === 'build_order' ? 'fa-list-ol' : 'fa-palette'}`} style={{ color: b.note.used === 'build_order' ? TEAL : MUTED, marginTop: '3px' }} />
                      <span>{b.note.says}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '20px' }}>
                <thead>
                  <tr style={{ color: MUTED }}>
                    {['', 'Part No.','Part Name','Total Qty','Finishes','Machine', ...(floorCycles.length > 0 ? ['Cycle'] : []), ...(bufferConflictPartIds.size > 0 ? ['Buffer'] : [])].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '5px 10px', borderBottom: `1px solid ${BORDER}`,
                        background: '#161b22', fontWeight: '700', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row) => {
                    const isFP = forcePrimary.has(row.part_id);
                    const isBufferConflict = bufferConflictPartIds.has(row.part_id);
                    const overrideVal = bufferOverrides[row.part_id] ?? '';
                    const alert = alertMap[row.part_id] || null;
                    const rowBg = alert
                      ? alert.level === 'conflict' ? '#ef444410' : '#f59e0b0e'
                      : 'transparent';
                    const iconColor = alert?.level === 'conflict' ? '#f87171' : '#fbbf24';
                    return (
                      <tr key={row.part_id} style={{ borderBottom: `1px solid ${BORDER}33`, background: rowBg }}>
                        {/* Status icon cell — hover triggers tooltip */}
                        <td style={{ padding: '7px 6px 7px 10px', width: '20px' }}
                          onMouseEnter={alert ? e => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setTooltip({ msgs: alert.msgs, level: alert.level, x: r.right + 10, y: r.top });
                          } : undefined}
                          onMouseLeave={alert ? () => setTooltip(null) : undefined}>
                          {alert && (
                            <i className={`fa-solid ${alert.level === 'conflict' ? 'fa-circle-xmark' : 'fa-triangle-exclamation'}`}
                              style={{ color: iconColor, fontSize: '12px', cursor: 'default' }} />
                          )}
                        </td>
                        <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: TEAL, fontSize: '11px' }}>{row.part_number || '—'}</td>
                        <td onClick={row.part_id ? () => openPartLink(row.part_id) : undefined}
                          title={row.part_id ? 'Open part in new window' : undefined}
                          style={{ padding: '7px 10px', fontWeight: '600', cursor: row.part_id ? 'pointer' : 'default' }}>
                          {row.part_name || `Part #${row.part_id}`}
                        </td>
                        <td style={{ padding: '7px 10px', color: isBufferConflict && row.totalQty === 0 ? '#ef4444' : MUTED }}>
                          {isBufferConflict && row.totalQty === 0 ? 'Not scheduled' : `${row.totalQty.toLocaleString()} pcs`}
                        </td>
                        <td style={{ padding: '7px 10px', color: '#f59e0b', fontWeight: '600' }}>
                          {row.totalQty === 0 && backlogParts.has(row.part_id)
                            ? <button onClick={() => toBacklog(backlogLines.filter(id => warnings.some(w => Number(w.line_id) === id && w.part_id === row.part_id)))}
                                title="It waits on the backlog beside the Schedule board, with why: drag it onto the board there"
                                style={{ background: 'none', border: 'none', padding: 0, color: TEAL, fontWeight: 600, fontSize: 'inherit', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                                On the backlog →
                              </button>
                            : finishLabel(row.finishDate)}
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          {(row.hasAlt || isFP) ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', userSelect: 'none' }}>
                              <input type="checkbox" checked={isFP} onChange={() => toggleForcePrimary(row.part_id)}
                                style={{ accentColor: TEAL, width: '13px', height: '13px', cursor: 'pointer' }} />
                              <span style={{ fontSize: '11px', color: isFP ? TEAL : '#f59e0b', fontWeight: '600' }}>
                                {isFP ? 'Primary only' : 'Alt. machine'}
                              </span>
                            </label>
                          ) : (
                            <span style={{ fontSize: '11px', color: TEAL }}>Primary</span>
                          )}
                        </td>
                        {floorCycles.length > 0 && (
                          <td style={{ padding: '7px 10px' }}>
                            {floorBy[row.part_id] ? (
                              <label title={floorBy[row.part_id].says} style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', userSelect: 'none' }}>
                                <input type="checkbox" checked={floorUse.has(row.part_id)}
                                  onChange={() => setFloorUse(prev => { const n = new Set(prev); n.has(row.part_id) ? n.delete(row.part_id) : n.add(row.part_id); return n; })}
                                  style={{ accentColor: TEAL, width: '13px', height: '13px', cursor: 'pointer' }} />
                                <span style={{ fontSize: '11px', fontWeight: '600', color: floorUse.has(row.part_id) ? TEAL : '#f59e0b', whiteSpace: 'nowrap' }}>
                                  {floorUse.has(row.part_id)
                                    ? `Floor ${floorBy[row.part_id].floor_cycle_s} s`
                                    : `${floorBy[row.part_id].planned_cycle_s} s · floor ${floorBy[row.part_id].floor_cycle_s} s`}
                                </span>
                              </label>
                            ) : <span style={{ fontSize: '11px', color: MUTED }}>as planned</span>}
                          </td>
                        )}
                        {bufferConflictPartIds.size > 0 && (
                          <td style={{ padding: '7px 10px' }}>
                            {isBufferConflict ? (
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input
                                  type="number" min="0" max="30"
                                  value={overrideVal}
                                  placeholder="days"
                                  onChange={e => {
                                    const v = e.target.value;
                                    setBufferOverrides(prev => {
                                      const next = { ...prev };
                                      if (v === '') delete next[row.part_id];
                                      else next[row.part_id] = Math.max(0, parseInt(v, 10) || 0);
                                      return next;
                                    });
                                  }}
                                  style={{ width: '52px', padding: '3px 6px', fontSize: '12px', background: '#0d1117',
                                    border: `1px solid ${overrideVal !== '' ? '#f59e0b' : BORDER}`, borderRadius: '5px',
                                    color: overrideVal !== '' ? '#fbbf24' : MUTED, outline: 'none' }}
                                />
                                <span style={{ fontSize: '10px', color: MUTED }}>days</span>
                              </label>
                            ) : (
                              <span style={{ fontSize: '11px', color: MUTED }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>

              <div style={{ fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: '8px' }}>Day-by-Day Breakdown</div>
              <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: '520px', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ color: MUTED }}>
                    {['Part No.','Part Name','Date','Machine','Qty','Shifts',''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${BORDER}`,
                        background: '#161b22', fontWeight: '700', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proposed.map((p, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${BORDER}22` }}>
                      <td style={{ padding: '5px 8px', fontFamily: 'monospace', color: TEAL, fontSize: '11px' }}>{p.part_number || '—'}</td>
                      <td onClick={p.part_id ? () => openPartLink(p.part_id) : undefined}
                        title={p.part_id ? 'Open part in new window' : undefined}
                        style={{ padding: '5px 8px', fontWeight: '600', cursor: p.part_id ? 'pointer' : 'default' }}>
                        {p.part_name || `#${p.part_id}`}
                      </td>
                      <td style={{ padding: '5px 8px', color: MUTED }}>{p.scheduled_date}</td>
                      <td style={{ padding: '5px 8px', color: MUTED }}>{p.machine_name || p.machine_id}</td>
                      <td style={{ padding: '5px 8px' }}>{p.target_qty?.toLocaleString()}</td>
                      <td style={{ padding: '5px 8px', color: MUTED }}>{p.shifts}</td>
                      <td style={{ padding: '5px 8px' }}>
                        {p.is_alternative_machine && <Badge colour="#f59e0b">Alt. machine</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </>
          )}

          {proposed.length === 0 && (
            <div style={{ color: MUTED, fontSize: '13px', textAlign: 'center', padding: '32px 0' }}>
              Nothing could be placed — {backlogLines.length ? 'the parts wait on the backlog, with why.' : 'check flagged rows above.'}
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Left: destructive cancel (import mode) or plain cancel */}
          {onCancelImport ? (
            <button onClick={onCancelImport}
              style={{ background: 'none', border: '1px solid #ef444466', color: '#f87171',
                borderRadius: '8px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', marginRight: 'auto' }}>
              <i className="fa-solid fa-trash" style={{ marginRight: '6px' }} />Cancel Import
            </button>
          ) : (
            <button onClick={onReject} style={{ background: 'none', border: `1px solid ${BORDER}`,
              color: MUTED, borderRadius: '8px', padding: '8px 20px', fontSize: '13px', cursor: 'pointer', marginRight: 'auto' }}>
              Cancel
            </button>
          )}
          {/* Middle: confirm without scheduling (import mode only) */}
          {onConfirmNoSchedule && (
            <button onClick={onConfirmNoSchedule}
              style={{ background: 'none', border: `1px solid ${BORDER}`, color: TEXT,
                borderRadius: '8px', padding: '8px 20px', fontSize: '13px', cursor: 'pointer' }}>
              Confirm, don't schedule
            </button>
          )}
          {/* Nothing placed: the way on is the backlog */}
          {proposed.length === 0 && backlogLines.length > 0 && (
            <button onClick={() => toBacklog()} style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              <i className="fa-solid fa-inbox" style={{ marginRight: '6px' }} />Open the backlog
            </button>
          )}
          {/* Right: confirm + schedule */}
          {proposed.length > 0 && (
            <button onClick={() => onAccept(proposed)} style={{ background: TEAL, border: 'none',
              color: '#fff', borderRadius: '8px', padding: '8px 20px', fontSize: '13px',
              fontWeight: '600', cursor: 'pointer' }}>
              Confirm & Add to Schedule
            </button>
          )}
        </div>
      </div>

      {/* Re-run banner — appears when user returns to tab after opening a part link */}
      {showRerunBanner && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 300,
          background: '#1c2333', borderTop: '1px solid #f59e0b66',
          padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
        }}>
          <i className="fa-solid fa-rotate" style={{ color: '#f59e0b', fontSize: '14px', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: '13px', color: '#f0f2f5' }}>
            Part data may have changed — re-run scheduling to update the results.
          </span>
          <button onClick={handleRerun}
            style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px',
              padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
            <i className="fa-solid fa-rotate" style={{ marginRight: '8px' }} />Re-run Scheduling
          </button>
          <button onClick={() => setShowRerunBanner(false)}
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: 0, flexShrink: 0 }}>
            ×
          </button>
        </div>
      )}

      {/* Hover tooltip — rendered outside the modal card so it isn't clipped by overflow */}
      {tooltip && (
        <div style={{
          position: 'fixed', top: tooltip.y, left: tooltip.x, zIndex: 300,
          background: '#1c2333', border: `1px solid ${tooltip.level === 'conflict' ? '#ef444466' : '#f59e0b66'}`,
          borderRadius: '8px', padding: '10px 14px', maxWidth: '300px', pointerEvents: 'none',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {tooltip.msgs.map((msg, i) => (
            <div key={i} style={{ fontSize: '12px', color: tooltip.level === 'conflict' ? '#f87171' : '#fbbf24',
              display: 'flex', gap: '8px', alignItems: 'flex-start',
              marginTop: i > 0 ? '6px' : 0 }}>
              <i className={`fa-solid ${tooltip.level === 'conflict' ? 'fa-circle-xmark' : 'fa-triangle-exclamation'}`}
                style={{ marginTop: '2px', flexShrink: 0 }} />
              <span>{msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared hook: fetch parts + products lists ────────────────────────────────

function usePartsProducts() {
  const [parts, setParts] = useState([]);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get('/api/v1/parts').then(r => setParts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/api/v1/products').then(r => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  return { parts, products };
}

// The price list (directors), by part or product: 'part:12' -> { price, unit }.
// An order line shows it, and keeps a price of its own only where one is typed
// that differs -- a special price for that order (15 Oct 2026: "the app knows
// it now, and the unit"). Anyone else gets an empty list.
function usePriceList() {
  const [prices, setPrices] = useState({});
  useEffect(() => {
    api.get('/api/v1/prices').then(r => setPrices(Object.fromEntries((Array.isArray(r.data) ? r.data : [])
      .filter(e => e.invoice_price != null).map(e => [`${e.kind}:${e.id}`, { price: Number(e.invoice_price), unit: e.invoice_unit || 'pcs' }])))).catch(() => {});
  }, []);
  return prices;
}
// What a line saves: nothing where the price box holds the price list's own price.
function linePrice(v, list) {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return null;
  return list && Math.abs(n - list.price) < 1e-9 ? null : n;
}
// The price box: the price list's price and unit, or the special price typed.
function PriceBox({ value, list, onChange, disabled, style }) {
  const typed = value !== null && value !== undefined && value !== '';
  const special = typed && list && Math.abs(parseFloat(value) - list.price) > 1e-9;
  const unit = list ? list.unit : 'pc';
  return (
    <div style={{ position: 'relative', ...style }}
      title={special ? `A special price for this order. The price list says € ${list.price} per ${unit}.`
        : list ? `From the price list (€ ${list.price} per ${unit}). Type another for a special price on this order.`
        : 'No price in the price list yet (Admin → Customers → Prices). One typed here holds for this order.'}>
      <input type="number" min="0" step="0.0001" disabled={disabled} placeholder="€"
        value={typed ? value : list ? list.price : ''}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
        style={{ background: '#0f1117', border: `1px solid ${special ? '#f59e0b' : BORDER}`, borderRadius: '7px', color: special ? '#f59e0b' : '#f0f2f5',
          fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box', textAlign: 'right', padding: `5px ${unit.length * 6 + 20}px 5px 8px`, fontFamily: 'system-ui' }} />
      <span style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', fontSize: '10.5px', color: MUTED, pointerEvents: 'none' }}>€/{unit}</span>
    </div>
  );
}
const priceKeyOf = (itemKey) => (itemKey && itemKey.startsWith('product:') ? `product:${itemKey.slice(8)}` : itemKey && itemKey.startsWith('part:') ? `part:${itemKey.slice(5)}` : null);

// ─── Manual order form ────────────────────────────────────────────────────────

function ManualForm({ onSaved }) {
  const thisWeek = isoWeek(new Date());
  const [form, setForm] = useState({
    customer: '', order_number: '', date_received: new Date().toISOString().split('T')[0],
    delivery_week: thisWeek, delivery_year: currentYear(), delivery_date: '', notes: '',
    schedule_intent: 'asap',
    multi_shift: false, limit_mould_changes: false,
    schedule_mode: 'asap', buffer_days: 2,
  });
  const [lines, setLines] = useState([{ item_key: '', quantity: '', unit_price: '' }]);
  const { parts, products } = usePartsProducts();
  const prices = usePriceList();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function setLine(i, field, val) {
    const next = lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l);
    setLines(next);
  }

  function addLine() { setLines([...lines, { item_key: '', quantity: '', unit_price: '' }]); }
  function removeLine(i) { setLines(lines.filter((_, idx) => idx !== i)); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const validLines = lines
        .filter(l => l.item_key && l.quantity)
        .map(l => {
          const quantity = parseInt(l.quantity);
          const unit_price = linePrice(l.unit_price, prices[priceKeyOf(l.item_key)]);
          if (l.item_key.startsWith('product:')) {
            return { product_id: l.item_key.slice(8), quantity, unit_price };
          }
          const partId = parseInt(l.item_key.slice(5));
          const part = parts.find(p => p.id === partId);
          return { part_id: partId, part_number: part?.part_number || '', quantity, unit_price };
        });

      if (validLines.length === 0) { setError('Add at least one line with part or product and quantity.'); setSaving(false); return; }

      const { data } = await api.post('/api/v1/orders', { ...form, lines: validLines });
      onSaved(data, form);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  const inp = {
    background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '8px',
    padding: '8px 12px', fontSize: '13px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box',
  };
  const label = { fontSize: '11px', fontWeight: '600', color: MUTED, marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '20px' }}>
        {[
          ['Customer', 'customer', 'text'],
          ['Order Number', 'order_number', 'text'],
          ['Date Received', 'date_received', 'date'],
        ].map(([lbl, key, type]) => (
          <div key={key}>
            <span style={label}>{lbl}</span>
            <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={inp} />
          </div>
        ))}
        <div>
          <span style={label}>Delivery Week</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input type="number" min="1" max="53" value={form.delivery_week}
              onChange={e => setForm(f => ({ ...f, delivery_week: parseInt(e.target.value) }))}
              style={{ ...inp, width: '70px' }} placeholder="Wk" />
            <input type="number" min="2024" max="2030" value={form.delivery_year}
              onChange={e => setForm(f => ({ ...f, delivery_year: parseInt(e.target.value) }))}
              style={{ ...inp }} placeholder="Year" />
          </div>
        </div>
        <div>
          <span style={label}>Specific Deadline Date <span style={{ fontWeight: '400', color: MUTED }}>(optional)</span></span>
          <input type="date" value={form.delivery_date || ''}
            onChange={e => {
              const d = e.target.value;
              if (d) {
                const parsed = new Date(d + 'T12:00:00Z');
                setForm(f => ({ ...f, delivery_date: d, delivery_week: isoWeek(parsed), delivery_year: parsed.getUTCFullYear() }));
              } else {
                setForm(f => ({ ...f, delivery_date: '' }));
              }
            }}
            style={inp} />
        </div>
      </div>

      {!form.delivery_week && !form.delivery_date && (
        <div style={{ marginBottom: '20px' }}>
          <ScheduleIntentField
            value={form.schedule_intent}
            onChange={v => setForm(f => ({ ...f, schedule_intent: v }))}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[['multi_shift','Multi-shift? (2 slots/day)'],['limit_mould_changes','Limit mould changes/day?']].map(([key, lbl]) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))}
              style={{ accentColor: TEAL, width: '15px', height: '15px' }} />
            {lbl}
          </label>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.schedule_mode === 'buffer'}
            onChange={e => setForm(f => ({ ...f, schedule_mode: e.target.checked ? 'buffer' : 'asap' }))}
            style={{ accentColor: TEAL, width: '15px', height: '15px' }} />
          <span>Finish{' '}
            <input type="number" min="1" max="30" value={form.buffer_days}
              onChange={e => setForm(f => ({ ...f, buffer_days: Math.max(1, parseInt(e.target.value) || 1) }))}
              disabled={form.schedule_mode !== 'buffer'}
              style={{ width: '42px', background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '4px', color: form.schedule_mode === 'buffer' ? TEXT : MUTED, padding: '2px 6px', fontSize: '13px', textAlign: 'center', MozAppearance: 'textfield' }} />
            {' '}days early
          </span>
        </label>
      </div>

      <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Order Lines
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
          <select value={line.item_key} onChange={e => setLine(i, 'item_key', e.target.value)}
            style={{ ...inp, flex: 2 }}>
            <option value="">Select part or product…</option>
            <optgroup label="── Parts ──">
              {parts.map(p => (
                <option key={`part:${p.id}`} value={`part:${p.id}`}>
                  {p.name}{p.part_number ? ` (${p.part_number})` : ''}
                </option>
              ))}
            </optgroup>
            {products.length > 0 && (
              <optgroup label="── Products ──">
                {products.map(prod => (
                  <option key={`product:${prod.id}`} value={`product:${prod.id}`} style={{ color: TEAL }}>
                    {prod.name || prod.code}{prod.name && prod.code ? ` (${prod.code})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <input type="number" min="1" placeholder="Qty" value={line.quantity}
            onChange={e => setLine(i, 'quantity', e.target.value)} style={{ ...inp, width: '90px' }} />
          <PriceBox value={line.unit_price} list={prices[priceKeyOf(line.item_key)]} disabled={!line.item_key}
            onChange={v => setLine(i, 'unit_price', v ?? '')} style={{ width: '120px', flexShrink: 0 }} />
          {lines.length > 1 && (
            <button type="button" onClick={() => removeLine(i)}
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}>
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={addLine}
        style={{ background: 'none', border: `1px dashed ${BORDER}`, color: MUTED, borderRadius: '8px',
          padding: '6px 14px', fontSize: '12px', cursor: 'pointer', width: '100%', marginBottom: '20px' }}>
        + Add line
      </button>

      <div>
        <span style={label}>Notes</span>
        <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          rows={2} style={{ ...inp, resize: 'vertical' }} />
      </div>

      {error && <div style={{ color: '#f87171', fontSize: '12px', marginTop: '10px' }}>{error}</div>}

      <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
        <button type="submit" disabled={saving}
          style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px',
            padding: '10px 24px', fontSize: '13px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Add to Schedule'}
        </button>
      </div>
    </form>
  );
}

// ─── Excel upload form ────────────────────────────────────────────────────────

// Infer ISO week number from a cell value (text, numeric, or Excel date serial)

// Parse hidden columns and rows directly from the XLSX ZIP XML because SheetJS !cols
// does not reliably expand range-based <col min="X" max="Y" hidden="1"> entries.
async function detectHiddenFromXlsx(arrayBuffer) {
  const hiddenCols = new Set();
  const hiddenRows = new Set();
  try {
    if (typeof DecompressionStream === 'undefined') return { hiddenCols, hiddenRows };
    const bytes = new Uint8Array(arrayBuffer);
    const dv = new DataView(arrayBuffer);
    let offset = 0;
    while (offset < bytes.length - 30) {
      // Scan for local file header signature PK\x03\x04
      if (dv.getUint32(offset, true) !== 0x04034b50) { offset++; continue; }
      const comprMethod    = dv.getUint16(offset + 8,  true);
      const compressedSize = dv.getUint32(offset + 18, true);
      const fileNameLen    = dv.getUint16(offset + 26, true);
      const extraLen       = dv.getUint16(offset + 28, true);
      const dataStart      = offset + 30 + fileNameLen + extraLen;
      const fileName       = new TextDecoder().decode(bytes.subarray(offset + 30, offset + 30 + fileNameLen));

      if (fileName === 'xl/worksheets/sheet1.xml' && compressedSize > 0) {
        const compressed = bytes.subarray(dataStart, dataStart + compressedSize);
        let xmlStr = '';
        if (comprMethod === 0) {
          xmlStr = new TextDecoder().decode(compressed);
        } else if (comprMethod === 8) {
          const ds = new DecompressionStream('deflate-raw');
          const writer = ds.writable.getWriter();
          writer.write(compressed); writer.close();
          const reader = ds.readable.getReader();
          const chunks = [];
          for (;;) { const { done, value } = await reader.read(); if (done) break; chunks.push(value); }
          const total = chunks.reduce((s, c) => s + c.length, 0);
          const out = new Uint8Array(total);
          let pos = 0; for (const c of chunks) { out.set(c, pos); pos += c.length; }
          xmlStr = new TextDecoder().decode(out);
        }
        if (xmlStr) {
          // Hidden columns: parse <cols><col min="M" max="N" hidden="1"/></cols>
          const colsM = xmlStr.match(/<cols>([\s\S]*?)<\/cols>/);
          if (colsM) {
            const colRe = /<col\b([^>]*)\/>/g; let m;
            while ((m = colRe.exec(colsM[1])) !== null) {
              if (!/\bhidden="1"/.test(m[1])) continue;
              const mn = m[1].match(/\bmin="(\d+)"/), mx = m[1].match(/\bmax="(\d+)"/);
              if (mn && mx) for (let i = +mn[1] - 1; i <= +mx[1] - 1; i++) hiddenCols.add(i);
            }
          }
          // Hidden rows: parse <row r="N" ... hidden="1">
          const rowRe = /<row\b([^>]*)>/g; let rm;
          while ((rm = rowRe.exec(xmlStr)) !== null) {
            if (!/\bhidden="1"/.test(rm[1])) continue;
            const rM = rm[1].match(/\br="(\d+)"/);
            if (rM) hiddenRows.add(+rM[1] - 1);
          }
        }
        break;
      }
      offset = compressedSize > 0 ? dataStart + compressedSize : dataStart + 1;
    }
  } catch (e) { console.warn('XLSX hidden detection:', e); }
  return { hiddenCols, hiddenRows };
}

export function ExcelForm({ onSaved }) {
  const [stage, setStage]               = useState('idle');   // 'idle' | 'assign' | 'preview'
  const [file, setFile]                 = useState(null);
  const [rawData, setRawData]           = useState(null);     // { allRows, hiddenCols: Set<number> }
  const [rawPreview, setRawPreview]     = useState(null);     // { displayRows, totalRows, maxCol }
  const [headerRowIdx, setHeaderRowIdx] = useState(0);        // 0-based index of the header row
  const [wizardStep, setWizardStep]     = useState('part_num'); // 'part_num' | 'part_name' | 'weeks'
  const [partNumCol, setPartNumCol]     = useState(null);
  const [partNameCol, setPartNameCol]   = useState(null);
  const [weekCols, setWeekCols]         = useState([]);       // [{ colIndex, weekNum }]
  const [hideHiddenCols, setHideHiddenCols] = useState(true);
  const [preview, setPreview]               = useState(null);
  const [catalogue, setCatalogue]           = useState({ byPartNum: {}, byCode: {} });
  const [catLoading, setCatLoading]         = useState(false);
  const [excluded, setExcluded]             = useState(new Set());
  const [form, setForm]                     = useState({ customer: '', order_number: '', delivery_year: currentYear(), multi_shift: false, limit_mould_changes: false, force_full: false, schedule_mode: 'asap', buffer_days: 2 });
  const [importing, setImporting]           = useState(false);
  const [error, setError]                   = useState('');
  const fileRef = useRef();

  function colLabel(idx) {
    let label = '', n = idx + 1;
    while (n > 0) { label = String.fromCharCode(64 + (n % 26 || 26)) + label; n = Math.floor((n - 1) / 26); }
    return label;
  }

  function readSheet(f) {
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
        if (!allRows.length) { setError('Empty sheet'); return; }

        // Try SheetJS !cols/!rows first; fall back to raw XLSX ZIP parsing if nothing detected
        let hiddenCols = new Set();
        let hiddenRows = new Set();
        (ws['!cols'] || []).forEach((col, i) => { if (col?.hidden) hiddenCols.add(i); });
        (ws['!rows'] || []).forEach((row, i) => { if (row?.hidden) hiddenRows.add(i); });
        if (hiddenCols.size === 0 && hiddenRows.size === 0) {
          const detected = await detectHiddenFromXlsx(ev.target.result);
          hiddenCols = detected.hiddenCols;
          hiddenRows = detected.hiddenRows;
        }

        let maxCol = 0;
        const nonEmptyColSet = new Set();
        allRows.forEach(row => {
          if (!Array.isArray(row)) return;
          row.forEach((v, i) => {
            if (v != null && String(v).trim()) {
              maxCol = Math.max(maxCol, i);
              nonEmptyColSet.add(i);
            }
          });
        });
        const emptyCols = new Set();
        for (let i = 0; i <= maxCol; i++) {
          if (!nonEmptyColSet.has(i)) emptyCols.add(i);
        }

        const SHOW = Math.min(allRows.length, 12);
        const displayRows = allRows.slice(0, SHOW).map(r => Array.isArray(r) ? [...r] : []);
        for (const m of (ws['!merges'] || [])) {
          if (m.s.r >= SHOW) continue;
          const val = allRows[m.s.r]?.[m.s.c] ?? null;
          for (let r = m.s.r; r <= Math.min(m.e.r, SHOW - 1); r++) {
            for (let c = m.s.c; c <= m.e.c; c++) {
              if (r === m.s.r && c === m.s.c) continue;
              if (!displayRows[r]) displayRows[r] = [];
              displayRows[r][c] = val;
            }
          }
        }

        // Auto-detect header row
        let bestRowIdx = 0;
        for (let r = 0; r < Math.min(allRows.length, 12); r++) {
          const row = allRows[r] || [];
          const hasPartKw = row.some(v => v != null && PART_NUM_KW.some(k => normH(v) === k || normH(v).startsWith(k)));
          const layout = detectLayout(row);
          if (hasPartKw || layout.weekCols.length >= 2) { bestRowIdx = r; break; }
        }

        // Auto-detect column roles from the header row
        const layout = detectLayout(allRows[bestRowIdx] || []);
        const initPartNumCol  = layout.partNumCol  !== -1 ? layout.partNumCol  : null;
        const initPartNameCol = layout.partNameCol !== -1 ? layout.partNameCol : null;
        const initWeekCols    = (layout.weekCols || []).map(w => ({ colIndex: w.colIndex, weekNum: w.weekNum }));

        // Start at the first step that still needs input
        let initStep = 'part_num';
        if (initPartNumCol !== null) initStep = 'part_name';
        if (initPartNumCol !== null && initWeekCols.length > 0) initStep = 'weeks';

        setRawData({ allRows, hiddenCols, hiddenRows, emptyCols });
        setRawPreview({ displayRows, totalRows: allRows.length, maxCol });
        setHeaderRowIdx(bestRowIdx);
        setPartNumCol(initPartNumCol);
        setPartNameCol(initPartNameCol);
        setWeekCols(initWeekCols);
        setWizardStep(initStep);
        setStage('assign');
        setError('');
      } catch (err) {
        setError('Could not parse file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(f);
  }

  function handleColumnClick(colIdx) {
    if (wizardStep === 'part_num') {
      setPartNumCol(colIdx);
      setPartNameCol(p => p === colIdx ? null : p);
      setWeekCols(prev => prev.filter(w => w.colIndex !== colIdx));
      setWizardStep('part_name');
    } else if (wizardStep === 'part_name') {
      if (colIdx === partNumCol) return;
      setPartNameCol(colIdx);
      setWeekCols(prev => prev.filter(w => w.colIndex !== colIdx));
      setWizardStep('weeks');
    } else if (wizardStep === 'weeks') {
      if (colIdx === partNumCol || colIdx === partNameCol) return;
      setWeekCols(prev => {
        if (prev.find(w => w.colIndex === colIdx)) return prev.filter(w => w.colIndex !== colIdx);
        const headerVal = rawPreview?.displayRows[headerRowIdx]?.[colIdx];
        return [...prev, { colIndex: colIdx, weekNum: toWeekNum(headerVal) }];
      });
    }
  }

  function updateWeekNum(colIdx, weekNum) {
    setWeekCols(prev => prev.map(w => w.colIndex === colIdx ? { ...w, weekNum } : w));
  }

  function buildPreviewData(hideHidden) {
    if (partNumCol == null) { setError('Select a Part Number column first.'); return false; }
    const activeWeeks = (hideHidden ? weekCols.filter(w => !rawData.hiddenCols.has(w.colIndex)) : weekCols)
      .filter(w => w.weekNum != null);
    if (activeWeeks.length === 0) { setError('Select at least one week column with a valid week number.'); return false; }

    const dataRows = rawData.allRows.slice(headerRowIdx + 1).filter((_, i) =>
      !hideHidden || !rawData.hiddenRows.has(headerRowIdx + 1 + i)
    );
    const parts = [], cells = {};
    for (const row of dataRows) {
      const raw = row[partNumCol];
      if (raw == null || String(raw).trim() === '') continue;
      const pn = String(raw).trim();
      // Skip rows where the part-number cell looks like a header label (e.g. "Mat.-Nr:")
      if (PART_NUM_KW.some(k => normH(pn) === k || normH(pn).startsWith(k))) continue;
      if (!cells[pn]) { parts.push(pn); cells[pn] = {}; }
      for (const { colIndex, weekNum } of activeWeeks) {
        const val = parseInt(row[colIndex]);
        if (val > 0) cells[pn][String(weekNum)] = (cells[pn][String(weekNum)] || 0) + val;
      }
    }

    if (parts.length === 0) { setError('No data rows found. Try adjusting the header row.'); return false; }

    const weeksSet = new Set();
    Object.values(cells).forEach(wks => Object.keys(wks).forEach(w => weeksSet.add(w)));
    const weeks = [...weeksSet].sort((a, b) => parseInt(a) - parseInt(b));

    setPreview({ parts, weeks, cells });
    setExcluded(new Set());
    setError('');
    return true;
  }

  function handleConfirm() {
    if (!buildPreviewData(hideHiddenCols)) return;
    setStage('preview');
    setCatLoading(true);
    Promise.all([api.get('/api/v1/parts'), api.get('/api/v1/products')])
      .then(([pr, prodr]) => {
        const byPartNum = {};
        (pr.data || []).forEach(p => { if (p.part_number) byPartNum[p.part_number] = { name: p.name }; });
        const byCode = {};
        (prodr.data || []).forEach(p => {
          if (p.code) byCode[p.code] = { name: p.name, partCount: (p.product_parts || []).length };
        });
        setCatalogue({ byPartNum, byCode });
      })
      .catch(() => {})
      .finally(() => setCatLoading(false));
  }

  function handleFile(f) {
    setFile(f);
    setStage('idle');
    setRawData(null);
    setRawPreview(null);
    setHeaderRowIdx(0);
    setPartNumCol(null);
    setPartNameCol(null);
    setWeekCols([]);
    setPreview(null);
    setCatalogue({ byPartNum: {}, byCode: {} });
    setExcluded(new Set());
    setError('');
    if (f) readSheet(f);
  }

  function togglePart(pn) {
    setExcluded(prev => { const n = new Set(prev); n.has(pn) ? n.delete(pn) : n.add(pn); return n; });
  }
  function toggleAll(selectAll) {
    setExcluded(selectAll ? new Set() : new Set(preview?.parts || []));
  }

  async function handleImport() {
    if (!preview) return;
    setImporting(true);
    setError('');
    try {
      const snapshot = Object.fromEntries(
        Object.entries(preview.cells).filter(([pn]) => !excluded.has(pn))
      );
      if (Object.keys(snapshot).length === 0) {
        setError('No parts selected — tick at least one part to import.');
        setImporting(false);
        return;
      }
      const { data } = await api.post('/api/v1/orders/import', { snapshot, ...form });
      onSaved(data, form);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
    }
  }

  // ── Derived values ──
  const maxCol = rawPreview?.maxCol ?? 0;
  const hiddenWeekColCount = weekCols.filter(w => rawData?.hiddenCols.has(w.colIndex)).length;
  const hasAnyHidden = (rawData?.hiddenCols.size ?? 0) > 0 || (rawData?.hiddenRows.size ?? 0) > 0;
  const canPreview = partNumCol !== null && weekCols.some(w => w.weekNum != null);

  function getColAccent(colIdx) {
    if (colIdx === partNumCol)  return TEAL;
    if (colIdx === partNameCol) return '#818cf8';
    if (weekCols.find(w => w.colIndex === colIdx)) return '#f59e0b';
    return null;
  }

  const WIZARD_STEPS = [
    { key: 'part_num',  label: 'Part Number',  color: TEAL },
    { key: 'part_name', label: 'Part Name',    color: '#818cf8' },
    { key: 'weeks',     label: 'Week Columns', color: '#f59e0b' },
  ];
  const stepDone = { part_num: partNumCol !== null, part_name: partNameCol !== null, weeks: weekCols.length > 0 };

  const inp = { background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const microBtn = { background: 'none', border: `1px solid ${BORDER}`, color: MUTED, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer', lineHeight: '1.6' };

  return (
    <div>
      {/* ── File picker ── */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Excel File</div>
        <div style={{ fontSize: '11px', color: MUTED, marginBottom: '10px', lineHeight: 1.6 }}>
          Columns are auto-detected — assign roles below, then preview.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0] || null)} />
          <button onClick={() => fileRef.current.click()}
            style={{ background: '#161b22', border: `1px solid ${BORDER}`, color: TEXT, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' }}>
            <i className="fa-solid fa-upload" style={{ marginRight: '8px', color: TEAL }} />
            {file ? file.name : 'Choose file…'}
          </button>
          {file && (
            <button onClick={() => { handleFile(null); fileRef.current.value = ''; }}
              style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: '12px' }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Assign stage: guided column picker ── */}
      {stage === 'assign' && rawPreview && (
        <div>
          {/* Wizard instruction card */}
          <div style={{ background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '10px' }}>
            {/* Step pills */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {WIZARD_STEPS.map(s => {
                const isActive = wizardStep === s.key;
                const isDone   = stepDone[s.key];
                return (
                  <button key={s.key} onClick={() => setWizardStep(s.key)}
                    style={{
                      background: isActive ? `${s.color}22` : 'transparent',
                      border: `1px solid ${isActive ? s.color : isDone ? s.color + '55' : BORDER}`,
                      color: isActive ? s.color : isDone ? s.color + 'bb' : '#4b5563',
                      borderRadius: '20px', padding: '3px 12px', fontSize: '11px',
                      fontWeight: isActive ? '600' : '400', cursor: 'pointer',
                    }}>
                    {isDone && !isActive ? '✓ ' : isActive ? '● ' : '○ '}{s.label}
                  </button>
                );
              })}
            </div>

            {/* Instruction + action row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: TEXT, flex: 1 }}>
                {wizardStep === 'part_num'  && 'Click the column containing part numbers'}
                {wizardStep === 'part_name' && 'Click the column containing part names'}
                {wizardStep === 'weeks' && (weekCols.length === 0
                  ? 'Click the columns with weekly order quantities'
                  : `${weekCols.length} week column${weekCols.length > 1 ? 's' : ''} selected — click to add more, click again to remove`
                )}
              </span>
              {wizardStep === 'part_name' && (
                <button onClick={() => { setPartNameCol(null); setWizardStep('weeks'); }} style={microBtn}>
                  Skip
                </button>
              )}
              {wizardStep === 'weeks' && (
                <button onClick={handleConfirm} disabled={!canPreview}
                  style={{ background: canPreview ? TEAL : '#1f2937', border: 'none', color: canPreview ? '#fff' : MUTED,
                    borderRadius: '8px', padding: '6px 16px', fontSize: '12px', fontWeight: '600',
                    cursor: canPreview ? 'pointer' : 'not-allowed' }}>
                  Preview →
                </button>
              )}
              {/* Header row stepper */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: MUTED, marginLeft: 'auto' }}>
                Header row:
                <button onClick={() => setHeaderRowIdx(r => Math.max(0, r - 1))} style={microBtn}>↑</button>
                <span style={{ minWidth: '16px', textAlign: 'center' }}>{headerRowIdx + 1}</span>
                <button onClick={() => setHeaderRowIdx(r => Math.min(rawPreview.displayRows.length - 1, r + 1))} style={microBtn}>↓</button>
              </div>
            </div>

            {/* Hidden rows/cols toggle — own row so it's always visible */}
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${BORDER}33`,
              display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', cursor: 'pointer', userSelect: 'none',
                color: MUTED }}>
                <input type="checkbox" checked={hideHiddenCols}
                  onChange={e => setHideHiddenCols(e.target.checked)}
                  style={{ accentColor: TEAL, cursor: 'pointer' }} />
                Hide hidden + empty columns
              </label>
              <span style={{ fontSize: '10px', color: '#4b5563' }}>
                {rawData
                  ? `${rawData.hiddenCols.size} hidden col${rawData.hiddenCols.size !== 1 ? 's' : ''}, ${rawData.emptyCols?.size ?? 0} empty col${(rawData.emptyCols?.size ?? 0) !== 1 ? 's' : ''}, ${rawData.hiddenRows.size} hidden row${rawData.hiddenRows.size !== 1 ? 's' : ''} detected`
                  : ''}
              </span>
            </div>
          </div>

          {error && <div style={{ color: '#f87171', fontSize: '12px', marginBottom: '8px' }}>{error}</div>}

          {/* Data table */}
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 340px)', minHeight: '280px', border: `1px solid ${BORDER}`, borderRadius: '8px' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '11px', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ padding: '6px 10px', position: 'sticky', top: 0, left: 0, zIndex: 4,
                    background: '#0f1117', borderBottom: `1px solid ${BORDER}`, borderRight: `1px solid ${BORDER}`,
                    color: MUTED, fontSize: '10px', fontWeight: '700' }}>#</th>
                  {Array.from({ length: maxCol + 1 }, (_, colIdx) => {
                    const isHidden = rawData?.hiddenCols.has(colIdx);
                    const isEmpty  = rawData?.emptyCols.has(colIdx);
                    if (hideHiddenCols && (isHidden || isEmpty)) return null;
                    const accent   = getColAccent(colIdx);
                    const wc       = weekCols.find(w => w.colIndex === colIdx);
                    return (
                      <th key={colIdx} onClick={() => handleColumnClick(colIdx)}
                        style={{
                          padding: '5px 8px', position: 'sticky', top: 0, zIndex: 3,
                          background: accent ? `${accent}28` : '#0f1117',
                          borderBottom: `2px solid ${accent || BORDER}`,
                          color: accent || MUTED,
                          textAlign: 'left', whiteSpace: 'nowrap',
                          minWidth: wc ? '56px' : '44px',
                          cursor: 'pointer', userSelect: 'none',
                        }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '10px', fontWeight: '700' }}>
                          {colLabel(colIdx)}
                        </div>
                        {wc && (
                          <div onClick={e => e.stopPropagation()} style={{ marginTop: '3px' }}>
                            <input type="number" min="1" max="53" value={wc.weekNum ?? ''}
                              onChange={e => updateWeekNum(colIdx, parseInt(e.target.value) || null)}
                              placeholder="Wk"
                              style={{ width: '40px', background: '#0a0e14', border: `1px solid #f59e0b55`,
                                borderRadius: '4px', color: '#f59e0b', padding: '2px 4px', fontSize: '10px', textAlign: 'center' }} />
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rawPreview.displayRows.map((row, rowIdx) => {
                  const isHdrRow  = rowIdx === headerRowIdx;
                  const isAbove   = rowIdx < headerRowIdx;
                  const isHiddenRow = rawData?.hiddenRows.has(rowIdx);
                  if (hideHiddenCols && isHiddenRow && !isHdrRow) return null;
                  return (
                    <tr key={rowIdx} style={{ opacity: isAbove ? 0.28 : isHiddenRow ? 0.4 : 1 }}>
                      <td style={{
                        padding: '4px 10px', position: 'sticky', left: 0, zIndex: 1,
                        background: isHdrRow ? '#1c2333' : '#161b22',
                        borderRight: `1px solid ${BORDER}`,
                        borderBottom: isHdrRow ? `2px solid ${BORDER}` : `1px solid ${BORDER}18`,
                        color: isHdrRow ? TEXT : MUTED, fontWeight: isHdrRow ? '700' : '400', fontSize: '10px',
                      }}>
                        {rowIdx + 1}
                      </td>
                      {Array.from({ length: maxCol + 1 }, (_, colIdx) => {
                        const isHiddenCol = rawData?.hiddenCols.has(colIdx);
                        const isEmptyCol  = rawData?.emptyCols.has(colIdx);
                        if (hideHiddenCols && (isHiddenCol || isEmptyCol)) return null;
                        const val    = row?.[colIdx];
                        const str    = val != null ? String(val).trim() : '';
                        const accent = getColAccent(colIdx);
                        return (
                          <td key={colIdx} onClick={() => handleColumnClick(colIdx)}
                            style={{
                              padding: isHdrRow ? '5px 8px' : '3px 8px',
                              maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              background: accent ? `${accent}18` : 'transparent',
                              color: accent ? accent : str ? (isHdrRow ? TEXT : '#8b949e') : '#2d3545',
                              fontWeight: isHdrRow ? '600' : '400',
                              fontFamily: typeof val === 'number' ? 'monospace' : 'inherit',
                              borderBottom: isHdrRow ? `2px solid ${BORDER}` : `1px solid ${BORDER}18`,
                              cursor: 'pointer', userSelect: 'none',
                            }}>
                            {str}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rawPreview.totalRows > rawPreview.displayRows.length && (
            <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '6px' }}>
              Showing first {rawPreview.displayRows.length} of {rawPreview.totalRows} rows
            </div>
          )}
        </div>
      )}

      {/* ── Preview stage ── */}
      {stage === 'preview' && preview && (
        <>
          <div style={{ background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '8px',
            padding: '12px 16px', marginBottom: '16px', fontSize: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ color: TEAL, fontWeight: '700', fontSize: '11px' }}>
                  {weekCols.length >= 2 ? 'Wide format' : 'List format'}
                </span>
                <span style={{ color: MUTED, fontSize: '11px' }}>
                  {preview.parts.length - excluded.size}/{preview.parts.length} parts selected · {preview.weeks.length} delivery weeks
                </span>
                {catLoading && (
                  <span style={{ color: MUTED, fontSize: '11px' }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '4px' }} />Loading names…
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {hasAnyHidden && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: MUTED, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={hideHiddenCols}
                      onChange={e => { const v = e.target.checked; setHideHiddenCols(v); buildPreviewData(v); }}
                      style={{ accentColor: TEAL, cursor: 'pointer' }} />
                    Hide hidden + empty rows/cols
                    {hiddenWeekColCount > 0 && <span style={{ color: '#4b5563' }}>({hiddenWeekColCount} hidden week col{hiddenWeekColCount !== 1 ? 's' : ''} excluded)</span>}
                  </label>
                )}
                <button onClick={() => setStage('assign')}
                  style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED, borderRadius: '6px',
                    padding: '4px 12px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  ← Change columns
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '11px', minWidth: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '4px 8px', borderBottom: `1px solid ${BORDER}` }}>
                      <input type="checkbox" checked={excluded.size === 0} onChange={e => toggleAll(e.target.checked)}
                        style={{ accentColor: TEAL, cursor: 'pointer' }} title="Select / deselect all" />
                    </th>
                    <th style={{ padding: '4px 10px', color: MUTED, textAlign: 'left', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' }}>Part</th>
                    {preview.weeks.map(w => (
                      <th key={w} style={{ padding: '4px 8px', color: MUTED, borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' }}>Wk {w}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.parts.map(pn => {
                    const isExcluded = excluded.has(pn);
                    const catProduct = catalogue.byCode[pn];
                    const catPart    = catalogue.byPartNum[pn];
                    const dbInfo     = catProduct || catPart;
                    return (
                      <tr key={pn} style={{ opacity: isExcluded ? 0.35 : 1 }}>
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          <input type="checkbox" checked={!isExcluded} onChange={() => togglePart(pn)}
                            style={{ accentColor: TEAL, cursor: 'pointer' }} />
                        </td>
                        <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                          <span style={{ fontFamily: 'monospace', color: TEAL, fontWeight: '600' }}>{pn}</span>
                          {dbInfo && <span style={{ color: MUTED }}> — {dbInfo.name}</span>}
                          {catProduct && (
                            <span style={{ background: '#f59e0b22', color: '#f59e0b', fontSize: '10px', fontWeight: '700',
                              padding: '1px 7px', borderRadius: '8px', marginLeft: '8px', border: '1px solid #f59e0b44' }}>
                              Product · {catProduct.partCount} {catProduct.partCount === 1 ? 'part' : 'parts'}
                            </span>
                          )}
                          {!dbInfo && !catLoading && (
                            <span style={{ color: '#4b5563', fontSize: '10px', marginLeft: '6px' }}>unknown</span>
                          )}
                        </td>
                        {preview.weeks.map(w => (
                          <td key={w} style={{ padding: '4px 8px', color: preview.cells[pn]?.[w] ? TEAL : MUTED, textAlign: 'center' }}>
                            {preview.cells[pn]?.[w]?.toLocaleString() || '—'}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Customer</div>
              <input value={form.customer} onChange={e => setForm(f => ({ ...f, customer: e.target.value }))} style={inp} placeholder="Optional" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Order Number</div>
              <input value={form.order_number} onChange={e => setForm(f => ({ ...f, order_number: e.target.value }))} style={inp} placeholder="Optional" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Delivery Year</div>
              <input type="number" min="2024" max="2030" value={form.delivery_year}
                onChange={e => setForm(f => ({ ...f, delivery_year: parseInt(e.target.value) }))} style={inp} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {[['multi_shift','Multi-shift?'],['limit_mould_changes','Limit mould changes/day?']].map(([key, lbl]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))}
                  style={{ accentColor: TEAL, width: '15px', height: '15px' }} />
                {lbl}
              </label>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.force_full} onChange={e => setForm(f => ({ ...f, force_full: e.target.checked }))}
                style={{ accentColor: '#f59e0b', width: '15px', height: '15px' }} />
              <span>Force full import <span style={{ fontSize: '11px', color: MUTED }}>(ignore previous snapshot)</span></span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.schedule_mode === 'buffer'}
                onChange={e => setForm(f => ({ ...f, schedule_mode: e.target.checked ? 'buffer' : 'asap' }))}
                style={{ accentColor: TEAL, width: '15px', height: '15px' }} />
              <span>Finish{' '}
                <input type="number" min="1" max="30" value={form.buffer_days}
                  onChange={e => setForm(f => ({ ...f, buffer_days: Math.max(1, parseInt(e.target.value) || 1) }))}
                  disabled={form.schedule_mode !== 'buffer'}
                  style={{ width: '42px', background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '4px', color: form.schedule_mode === 'buffer' ? TEXT : MUTED, padding: '2px 6px', fontSize: '13px', textAlign: 'center', MozAppearance: 'textfield' }} />
                {' '}days early
              </span>
            </label>
          </div>
        </>
      )}

      {error && <div style={{ color: '#f87171', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}

      {stage === 'preview' && preview && (
        <button onClick={handleImport} disabled={importing}
          style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px',
            padding: '10px 24px', fontSize: '13px', fontWeight: '600',
            cursor: importing ? 'not-allowed' : 'pointer', opacity: importing ? 0.6 : 1 }}>
          {importing ? 'Importing…' : 'Import & Detect Changes'}
        </button>
      )}
    </div>
  );
}

// ─── Orders list ──────────────────────────────────────────────────────────────

// Build a flat render list from order lines — groups consecutive lines that share
// a product under a product-header item, standalone part lines appear as-is.
// A fixed order, whatever order the database returns the lines in (2 Oct 2026:
// saving one line moved it, and a product's lines could end up apart -- shown
// under another product's heading). Lines in the order they were added; a
// product's lines together, where its first line falls.
function buildRenderItems(lines) {
  const sorted = [...(lines || [])].sort((a, b) => a.id - b.id);
  const groups = [];
  const byProduct = new Map();
  for (const line of sorted) {
    const pid = line.product?.id;
    if (pid) {
      if (!byProduct.has(pid)) {
        const g = { product: line.product, lines: [] };
        byProduct.set(pid, g);
        groups.push(g);
      }
      byProduct.get(pid).lines.push(line);
    } else {
      groups.push({ product: null, lines: [line] });
    }
  }
  const items = [];
  let seq = 0;
  for (const g of groups) {
    if (g.product) items.push({ type: 'product-header', product: g.product });
    for (const line of g.lines) items.push({ type: 'line', line, seq: ++seq, isChild: !!g.product });
  }
  return items;
}

// The dispatch calendar (the director, 2 Nov 2026): every order's truck
// loading, and the Friday before each one (3 Nov), in his phone's calendar --
// kept up to date by MouldOS through one private link. Make it, add it to the
// iPhone (or iCloud, for every device), or replace it, which stops the old one.
function CalendarLinkModal({ onClose }) {
  const { toast, confirm } = useToast();
  const [state, setState] = useState(null);   // { link, made_at, made_by } or { link: null }
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/api/v1/calendar/link').then(r => setState(r.data))
      .catch(err => { toast.error(err.response?.data?.error || 'The calendar link could not be read just now.'); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  function make(replacing) {
    if (!replacing) { save(false); return; }
    confirm({
      message: 'Replace the calendar link? The old one stops working at once, so any calendar using it has to be added again with the new one.',
      confirmLabel: 'Replace the link', danger: true, onConfirm: () => save(true),
    });
  }
  async function save(replacing) {
    setBusy(true);
    try {
      const r = await api.post('/api/v1/calendar/link');
      setState(r.data);
      toast.success(replacing ? 'New link made; the old one no longer works' : 'Calendar link made');
    } catch (err) {
      toast.error(err.response?.data?.error || 'The link could not be made just now.');
    }
    setBusy(false);
  }
  async function copy() {
    try { await navigator.clipboard.writeText(state.link.https); toast.success('Link copied'); }
    catch { window.prompt('Copy this link:', state.link.https); }
  }
  const btn = (primary) => ({ background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL, border: `1px solid ${TEAL}`,
    borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' });
  const step = { fontSize: '12.5px', color: TEXT, margin: '4px 0', lineHeight: 1.5 };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#1e2329', border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '20px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', color: TEXT }}>
        <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
          <i className="fa-solid fa-calendar-plus" style={{ color: TEAL, marginRight: '8px' }} />Dispatches in your calendar
        </div>
        <div style={{ fontSize: '12.5px', color: MUTED, marginBottom: '14px', lineHeight: 1.5 }}>
          Every order's truck loading, 8:00 for three hours, marked (Confirmed) or (Suggested); and on the Friday before each one,
          "Truck loading on Monday: Get it ready" with alerts at 10:00 and 21:00. When a date changes here, the same event moves.
          Your calendar looks for changes about every hour.
        </div>
        {!state ? <div style={{ color: MUTED, fontSize: '13px' }}>Reading…</div> : !state.link ? (
          <button style={btn(true)} disabled={busy} onClick={() => make(false)}>
            <i className="fa-solid fa-link" />{busy ? 'Making it…' : 'Make my calendar link'}
          </button>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <a href={state.link.webcal} style={btn(true)}><i className="fa-brands fa-apple" />Add to this iPhone or Mac</a>
              <button style={btn(false)} onClick={copy}><i className="fa-solid fa-copy" />Copy the link</button>
            </div>
            <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '2px' }}>On your iPhone</div>
            <p style={step}>Open this page on the iPhone and tap <b>Add to this iPhone or Mac</b>, then <b>Subscribe</b>.</p>
            <p style={step}>On the next screen switch <b>Remove Alerts</b> off, or the Friday alerts will not come. Then <b>Add</b>.</p>
            <div style={{ fontWeight: 700, fontSize: '13px', margin: '10px 0 2px' }}>On every Apple device at once</div>
            <p style={step}>On a Mac: Calendar → File → New Calendar Subscription, paste the link, and choose <b>iCloud</b> as its location.</p>
            <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '12px', lineHeight: 1.5 }}>
              The link is private: anyone who has it can see the dispatch dates. Made {state.made_at ? new Date(state.made_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}{state.made_by ? ` by ${state.made_by}` : ''}.{' '}
              <button onClick={() => make(true)} disabled={busy} style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', padding: 0, fontSize: '11.5px', fontWeight: 700 }}>
                Replace the link
              </button>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #374151', color: '#9ca3af', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// NEW: kept when the orders were put back to an earlier point, although taken
// or added after it (the director, 6 Oct 2026). His to dismiss once seen.
function NewBadge({ onDismiss, what }) {
  const act = (e) => { e.stopPropagation(); if (onDismiss) onDismiss(); };
  return (
    <span role={onDismiss ? 'button' : undefined} tabIndex={onDismiss ? 0 : undefined}
      onClick={act} onKeyDown={e => { if (e.key === 'Enter') act(e); }}
      title={`${what} after the point the orders were put back to, and kept.${onDismiss ? ' Click to dismiss.' : ''}`}
      style={{ fontSize: '9px', fontWeight: '800', letterSpacing: '0.05em', padding: '1px 6px', borderRadius: '4px', flexShrink: 0,
               display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '6px', verticalAlign: 'middle',
               cursor: onDismiss ? 'pointer' : 'default',
               background: 'rgba(20,184,166,0.18)', color: '#5eead4', border: '1px solid rgba(20,184,166,0.55)' }}>
      NEW{onDismiss && <i className="fa-solid fa-xmark" style={{ fontSize: '8px', opacity: 0.75 }} />}
    </span>
  );
}

// What can be done with one order line, from its status (15 Oct 2026). Fixed
// to the screen beside the badge; closes on a click elsewhere or Escape.
function LineMenu({ at, onClose, onBoard, onDone, onStock, onBacklog }) {
  const ref = useRef(null);
  useEffect(() => {
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', out);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', onClose, true);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); window.removeEventListener('scroll', onClose, true); };
  }, [onClose]);
  // The runs still to finish, one entry each.
  const runs = [];
  for (const r of at.line.board_rows || []) {
    if (r.done || !r.plan_id) continue;
    const run = runs.find(x => x.plan_id === r.plan_id);
    if (run) run.last = r.date; else runs.push({ plan_id: r.plan_id, first: r.date, last: r.date });
  }
  const day = (iso) => new Date(iso + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const item = { display: 'flex', gap: '10px', alignItems: 'flex-start', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: TEXT, padding: '8px 10px', fontSize: '13px', cursor: 'pointer', borderRadius: '6px', fontFamily: 'system-ui' };
  const sub = { display: 'block', fontSize: '11px', color: MUTED, marginTop: '1px' };
  const width = 280;
  return (
    <div ref={ref} onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: Math.min(at.rect.bottom + 4, window.innerHeight - 240), left: Math.max(8, Math.min(at.rect.left - 100, window.innerWidth - width - 8)), width,
      background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', padding: '6px', zIndex: 80 }}>
      <div style={{ fontSize: '11px', color: MUTED, padding: '4px 10px 6px' }}>{at.line.part?.name || at.line.part_number}</div>
      {onBoard && (
        <button style={item} onClick={() => { onClose(); onBoard(); }}>
          <i className="fa-solid fa-table-cells" style={{ color: TEAL, marginTop: '2px', width: '14px' }} />
          <span>Show on the board<span style={sub}>Its run, ringed</span></span>
        </button>
      )}
      {onDone && runs.map(run => (
        <button key={run.plan_id} style={item} onClick={() => onDone(run)}>
          <i className="fa-solid fa-check" style={{ color: '#4ade80', marginTop: '2px', width: '14px' }} />
          <span>It has run — mark done<span style={sub}>{runs.length > 1 ? `The run of ${day(run.first)}${run.last !== run.first ? `–${day(run.last)}` : ''}: ` : ''}how many good parts, and up to which day</span></span>
        </button>
      ))}
      {onStock && (
        <button style={item} onClick={onStock}>
          <i className="fa-solid fa-box" style={{ color: '#93c5fd', marginTop: '2px', width: '14px' }} />
          <span>Supplied from stock…<span style={sub}>{at.isDone ? 'Covered, but days are still planned — see what comes off' : 'How many pieces came from stock'}</span></span>
        </button>
      )}
      {onBacklog && (
        <button style={item} onClick={() => { onClose(); onBacklog(); }}>
          <i className="fa-solid fa-inbox" style={{ color: TEAL, marginTop: '2px', width: '14px' }} />
          <span>Place it from the backlog<span style={sub}>It is not on the board: the board opens with it in the backlog, with why</span></span>
        </button>
      )}
      {!onBoard && !(onDone && runs.length) && !onStock && !onBacklog && <div style={{ fontSize: '12px', color: MUTED, padding: '6px 10px' }}>Nothing to do for this line here.</div>}
    </div>
  );
}

function OrderCard({ order, onSchedule, onDelete, onDeleteLine, onEditLine, onRename, onFulfill, onDispatch, onLineDispatch, onLineStock, onEdit, readOnly, completed = false, onReopen, canSeeHistory = false, onDismissNew, startOpen = false, onRunDone, invoices = [] }) {
  const navigate = useNavigate();
  // The line being marked from stock, in the pop-up that draws what it does
  // to the board (15 Oct 2026); the gap a run that finished early left.
  const [stockLine, setStockLine] = useState(null);   // { line, start }
  const [gapOffer, setGapOffer] = useState(null);
  // Days of the line still to run on the board -- a covered line can still have them.
  const stillPlanned = (line) => (line.board_rows || []).some(r => !r.done && !r.started);
  const openStock = (line, covered) => setStockLine({ line, start: line.from_stock_qty || Math.max(0, (line.quantity || 0) - covered) });
  const [expanded, setExpanded] = useState(startOpen);
  const [editingLine, setEditingLine] = useState(null); // { id, qty }
  const [renamingOrder, setRenamingOrder] = useState(false);
  const [renameForm, setRenameForm] = useState({ customer: order.customer || '', order_number: order.order_number || '' });
  const [editingDispatch, setEditingDispatch] = useState(false); // order-level date picker open
  const [editingLineDispatch, setEditingLineDispatch] = useState(null); // lineId whose date picker is open
  // A line's run on the board: the next day still to run, else its last day,
  // with every one of its cards ringed when the board opens.
  const toBoard = (line) => {
    const rows = line.board_rows || [];
    if (!rows.length) return null;
    const at = (rows.find(r => !r.done) || rows[rows.length - 1]).date;
    return () => navigate(`/admin/schedule?at=${at}&rows=${rows.map(r => r.id).join(',')}`);
  };
  // A line's status opens what can be done with it: see its run on the board,
  // say its run has run and is done, or that it came from stock.
  const { toast } = useToast();
  const [lineMenu, setLineMenu] = useState(null);     // { line, covered, isDone, rect }
  const [completing, setCompleting] = useState(null); // the run being marked done
  const statusLink = (badge, line, covered, isDone) => {
    const canAct = (onRunDone || onLineStock) && (!isDone || stillPlanned(line));
    if (!(line.board_rows || []).length && !canAct) return badge;
    return (
      <button onClick={e => { e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); setLineMenu(m => (m && m.line.id === line.id ? null : { line, covered, isDone, rect })); }}
        title="What to do with this line" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{badge}</button>
    );
  };
  const totalParts = (order.lines || []).reduce((s, l) => s + (l.quantity || 0), 0);
  const scheduled  = (order.lines || []).filter(l => !needsPlan(l)).length;
  const allSched   = scheduled === (order.lines || []).length && (order.lines || []).length > 0;

  function saveEdit(lineId) {
    const qty = parseInt(editingLine.qty);
    if (qty >= 1) onEditLine(lineId, qty);
    setEditingLine(null);
  }

  function startRename(e) {
    e.stopPropagation();
    setRenameForm({ customer: order.customer || '', order_number: order.order_number || '' });
    setRenamingOrder(true);
  }

  function saveRename(e) {
    e.stopPropagation();
    onRename(order.id, renameForm);
    setRenamingOrder(false);
  }

  function cancelRename(e) {
    e.stopPropagation();
    setRenamingOrder(false);
  }

  return (
    <div id={`order-${order.id}`} style={{ background: completed ? '#191e24' : CARD, border: `1px solid ${BORDER}`, borderRadius: '10px',
      marginBottom: '10px', overflow: 'hidden', opacity: completed ? 0.92 : 1 }}>
      {/* Card header — flex so it wraps naturally on mobile */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px',
        cursor: 'pointer' }}
        onClick={() => !renamingOrder && setExpanded(e => !e)}>

        {/* Left: title + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {renamingOrder ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}
              onClick={e => e.stopPropagation()}>
              <input
                value={renameForm.customer}
                onChange={e => setRenameForm(f => ({ ...f, customer: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') saveRename(e); if (e.key === 'Escape') cancelRename(e); }}
                placeholder="Customer"
                autoFocus
                style={{ background: '#161b22', border: `1px solid ${TEAL}`, borderRadius: '6px',
                  color: TEXT, padding: '4px 8px', fontSize: '13px', fontWeight: '700', width: '130px' }}
              />
              <input
                value={renameForm.order_number}
                onChange={e => setRenameForm(f => ({ ...f, order_number: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') saveRename(e); if (e.key === 'Escape') cancelRename(e); }}
                placeholder="Order #"
                style={{ background: '#161b22', border: `1px solid ${TEAL}`, borderRadius: '6px',
                  color: TEXT, padding: '4px 8px', fontSize: '13px', fontWeight: '700', width: '100px' }}
              />
              <button onClick={saveRename}
                style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '5px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>✓</button>
              <button onClick={cancelRename}
                style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED, borderRadius: '5px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}>✕</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '14px', fontWeight: '700' }}>
                {order.customer || 'Order'} {order.order_number ? `#${order.order_number}` : `#${order.id}`}
              </span>
              {order.new_since_revert && (
                <NewBadge what="Taken" onDismiss={onDismissNew ? () => onDismissNew({ orders: [order.id] }) : undefined} />
              )}
              {!readOnly && (
                <button onClick={startRename}
                  style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer',
                    padding: '2px 4px', fontSize: '11px', lineHeight: 1, opacity: 0.6 }}
                  title="Rename order">
                  <i className="fa-solid fa-pen" />
                </button>
              )}
            </div>
          )}
          {completed ? (
          <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: '600' }}>
              <i className="fa-solid fa-circle-check" style={{ marginRight: '4px', fontSize: '10px' }} />
              Completed {order.fulfilled_at ? new Date(order.fulfilled_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
            </span>
            <span>·</span>
            <span>{order.delivery_week || order.delivery_date ? formatDeadline(order.delivery_week, order.delivery_year, order.delivery_date) : 'no delivery week'}</span>
            <span>·</span>
            <span>
              <i className="fa-solid fa-truck" style={{ marginRight: '4px', fontSize: '9px' }} />
              {order.dispatch_date ? `Dispatch ${fmtDate(order.dispatch_date)}`
                : order.suggested_dispatch_date ? `Dispatch ${fmtDate(order.suggested_dispatch_date)} (not confirmed)` : 'no dispatch date'}
            </span>
            <span>·</span>
            <span>{totalParts.toLocaleString()} parts</span>
          </div>
          ) : (
          <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            <span>Received {order.date_received}</span>
            <span>·</span>
            {order.delivery_week || order.delivery_date ? (
              <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                {formatDeadline(order.delivery_week, order.delivery_year, order.delivery_date)}
              </span>
            ) : (
              <ScheduleIntentBadge order={order} />
            )}
            <span>·</span>
            <span>{totalParts.toLocaleString()} parts · {order.source}</span>
          </div>
          )}
          {/* A second row (the director, 30 Oct 2026: "Truck and Invoice in a second
              row, I think it will be clearer"): the truck -- where each pallet
              stands, marked done by clicking it -- for everyone; the invoice and
              delivery notes for directors. */}
          {/* Completed orders too (the director, 2 Nov 2026). */}
          {!renamingOrder && ((order.lines || []).length > 0 || canSeeHistory) && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }} onClick={e => e.stopPropagation()}>
              {(order.lines || []).length > 0 && (
                <button onClick={() => navigate(`/orders/${order.id}/truck`)}
                  style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.7)', color: '#7dd3fc',
                    borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '700',
                    cursor: 'pointer', whiteSpace: 'nowrap' }}
                  title="The lorry for this order: where each pallet stands, and which are done">
                  <i className="fa-solid fa-truck-ramp-box" style={{ marginRight: '5px' }} />Truck
                </button>
              )}
              {/* An order already invoiced opens its invoice (the director, 2 Nov
                  2026: "it should open existing invoices if they have already
                  issued invoice"); another, for a second dispatch, is the small
                  + beside it. */}
              {canSeeHistory && invoices.map(inv => (
                <button key={inv.id} onClick={() => openInvoicePdf(inv, toast)}
                  style={{ background: 'rgba(20,184,166,0.08)', border: '1px solid rgba(20,184,166,0.7)', color: '#5eead4',
                    borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '700',
                    cursor: 'pointer', whiteSpace: 'nowrap' }}
                  title="Open the invoice and its two delivery notes">
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '5px' }} />Invoice #{inv.number}
                </button>
              ))}
              {canSeeHistory && (invoices.length ? (
                <button onClick={() => navigate(`/admin/invoices/new?order=${order.id}`)}
                  style={{ background: 'transparent', border: '1px solid #374151', color: MUTED,
                    borderRadius: '6px', padding: '4px 8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                  title="Another invoice for this order, only for a second dispatch" aria-label="Another invoice for this order">
                  <i className="fa-solid fa-plus" />
                </button>
              ) : (
                <button onClick={() => navigate(`/admin/invoices/new?order=${order.id}`)}
                  style={{ background: 'rgba(20,184,166,0.08)', border: '1px solid rgba(20,184,166,0.7)', color: '#5eead4',
                    borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '700',
                    cursor: 'pointer', whiteSpace: 'nowrap' }}
                  title="Prepare the invoice and delivery notes for this order">
                  <i className="fa-solid fa-file-invoice" style={{ marginRight: '5px' }} />Invoice
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: actions — flex row, wraps on narrow screens */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}
          onClick={e => e.stopPropagation()}>

          {completed ? (
            <>
              {order.verdict && (
                <span title="When its runs really ran, against the date it was promised for"
                  style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                    borderRadius: '6px', padding: '3px 7px',
                    color: order.verdict.stamp === 'ok' ? '#4ade80' : order.verdict.stamp === 'late' ? '#f87171' : MUTED,
                    border: `1px solid ${order.verdict.stamp === 'ok' ? '#4ade8066' : order.verdict.stamp === 'late' ? '#f8717166' : BORDER}` }}>
                  {order.verdict.label}
                </span>
              )}
              <span style={{ background: '#4ade80', color: '#052e16', fontSize: '9px', fontWeight: '700',
                padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>Completed</span>
              {onReopen && (
                <button onClick={onReopen} title="Move it back to the active orders"
                  style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED, borderRadius: '6px',
                    padding: '3px 9px', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <i className="fa-solid fa-rotate-left" style={{ marginRight: '4px', fontSize: '9px' }} />Reopen
                </button>
              )}
            </>
          ) : (<>
          {/* Dispatch chip */}
          {!readOnly && (() => {
            const dispDate = order.dispatch_date || order.suggested_dispatch_date;
            const confirmed = !!order.dispatch_date;
            if (!dispDate) return null;
            if (editingDispatch) return (
              <input type="date" defaultValue={dispDate} autoFocus
                onBlur={e => { setEditingDispatch(false); if (e.target.value && e.target.value !== dispDate) onDispatch(order.id, e.target.value); }}
                onKeyDown={e => { if (e.key === 'Escape') setEditingDispatch(false); if (e.key === 'Enter') { setEditingDispatch(false); onDispatch(order.id, e.target.value); } }}
                style={{ background: '#161b22', border: `1px solid ${TEAL}`, borderRadius: '6px', color: TEXT, padding: '3px 4px', fontSize: '10px', outline: 'none', width: '110px', boxSizing: 'border-box' }}
              />
            );
            return (
              <div onClick={() => setEditingDispatch(true)}
                title={confirmed ? 'Click to change dispatch date' : 'Suggested dispatch date — click to confirm or change'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: `${TEAL}12`,
                  border: `1px ${confirmed ? 'solid' : 'dashed'} ${TEAL}${confirmed ? '8c' : '59'}`,
                  borderRadius: '6px', padding: '3px 6px', fontSize: '9px', fontWeight: '600',
                  color: TEAL, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                <i className="fa-solid fa-truck" style={{ flexShrink: 0 }} />
                <span>{fmtDate(dispDate)}</span>
                {confirmed && (
                  <span onClick={e => { e.stopPropagation(); onDispatch(order.id, null); }}
                    style={{ marginLeft: '1px', color: '#6b7280', fontWeight: '400', fontSize: '9px' }}
                    title="Reset to suggested date">✕</span>
                )}
              </div>
            );
          })()}

          {/* Status / Schedule button */}
          {allSched
            ? <Badge colour={TEAL}>Scheduled</Badge>
            : readOnly
              ? <Badge colour={scheduled > 0 ? '#f59e0b' : MUTED}>
                  {scheduled > 0 ? `${scheduled}/${(order.lines||[]).length}` : 'Pending'}
                </Badge>
              : (() => {
                  const partial = scheduled > 0;
                  return (
                    <button onClick={e => { e.stopPropagation(); onSchedule(order); }}
                      style={{
                        background: partial ? 'rgba(245,158,11,0.12)' : `${TEAL}26`,
                        border: `1px solid ${partial ? 'rgba(245,158,11,0.45)' : `${TEAL}80`}`,
                        color: partial ? '#f59e0b' : TEAL,
                        borderRadius: '6px', padding: '3px 9px', fontSize: '10px', fontWeight: '700',
                        cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.4,
                      }}>
                      {partial ? `↺ Schedule · ${scheduled}/${(order.lines||[]).length}` : 'Schedule'}
                    </button>
                  );
                })()
          }

          {/* Complete button */}
          {!readOnly && onFulfill && (
            // Outlined while it is still to do; "Completed" is the solid one (2 Oct 2026).
            <button onClick={e => { e.stopPropagation(); onFulfill(); }}
              style={{ background: 'transparent', border: '1px solid #4ade80', color: '#4ade80',
                borderRadius: '6px', padding: '3px 8px', fontSize: '9px', fontWeight: '700',
                cursor: 'pointer', whiteSpace: 'nowrap' }}
              title="Mark as complete">
              ✓ Complete
            </button>
          )}

          {/* Edit */}
          {!readOnly && onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit(order); }}
              style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED, cursor: 'pointer',
                padding: '4px 8px', fontSize: '11px', borderRadius: '6px', lineHeight: 1 }}
              title="Edit order">
              <i className="fa-solid fa-pen-to-square" />
            </button>
          )}

          {/* Delete */}
          {!readOnly && (
            <button onClick={e => { e.stopPropagation(); onDelete(order.id); }}
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer',
                padding: '4px 4px', fontSize: '13px', lineHeight: 1 }}
              title="Delete order">
              <i className="fa-regular fa-trash-can" />
            </button>
          )}
          </>)}
        </div>

        {/* Chevron */}
        <i className={`fa-solid fa-chevron-${expanded ? 'up' : 'down'}`}
          style={{ color: MUTED, fontSize: '11px', flexShrink: 0, pointerEvents: 'none' }} />
      </div>

      {lineMenu && (
        <LineMenu at={lineMenu} onClose={() => setLineMenu(null)}
          onBoard={toBoard(lineMenu.line)}
          onDone={onRunDone && !lineMenu.isDone ? (run) => { setLineMenu(null); setCompleting({ planId: run.plan_id, partName: lineMenu.line.part?.name || lineMenu.line.part_number || 'Job', totalQty: 0, goodPartsMade: 0 }); } : null}
          onStock={onLineStock && !readOnly && (!lineMenu.isDone || stillPlanned(lineMenu.line)) ? () => { setLineMenu(null); openStock(lineMenu.line, lineMenu.covered); } : null}
          onBacklog={!readOnly && !lineMenu.isDone && !stillPlanned(lineMenu.line) ? () => navigate(`/admin/schedule?backlog=1&lines=${lineMenu.line.id}`) : null} />
      )}
      {completing && (
        <CompletePlanModal plan={completing} toast={toast} onClose={() => setCompleting(null)}
          onDone={(data) => { setCompleting(null); onRunDone && onRunDone(); if (data?.close_gap) setGapOffer(data.close_gap); }} />
      )}
      {gapOffer && (
        <GapModal offer={gapOffer} heading="It finished early" toast={toast}
          onClose={() => setGapOffer(null)}
          onDone={() => { setGapOffer(null); onRunDone && onRunDone(); }} />
      )}
      {stockLine && (
        <StockModal line={stockLine.line} startQty={stockLine.start} toast={toast}
          title={[order.order_number || `Order ${order.id}`, stockLine.line.product?.name, stockLine.line.part?.name || stockLine.line.part_number].filter(Boolean).join(' · ')}
          onClose={() => setStockLine(null)}
          onShowBoard={toBoard(stockLine.line)}
          onSaved={() => { setStockLine(null); onRunDone && onRunDone(); }} />
      )}
      {expanded && (
        <div style={{ borderTop: `1px solid ${BORDER}`, overflowX: 'auto' }}>
          <div style={{ minWidth: '860px' }}>
          {/* Column header */}
          {(() => {
            const C = '24px 80px minmax(100px,1fr) 72px 64px 110px 90px 90px 28px 28px 22px 24px';
            return (
              <div style={{ display: 'grid', gridTemplateColumns: C, columnGap: '8px',
                padding: '5px 16px', background: '#161b22', alignItems: 'center',
                fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
                letterSpacing: '0.06em', color: MUTED }}>
                <div>#</div>
                <div>Part No.</div>
                <div>Name</div>
                <div style={{ textAlign: 'center' }}>Ordered</div>
                <div style={{ textAlign: 'center' }}>Made</div>
                <div style={{ textAlign: 'center' }}>Deadline</div>
                <div style={{ textAlign: 'center' }}>Status</div>
                <div style={{ textAlign: 'center' }}>Done</div>
                <div style={{ textAlign: 'center' }}>
                  <i className="fa-solid fa-truck" style={{ fontSize: '9px' }} />
                </div>
                <div /><div /><div />
              </div>
            );
          })()}

          {/* Grouped line rows */}
          {(() => {
            const C = '24px 80px minmax(100px,1fr) 72px 64px 110px 90px 90px 28px 28px 22px 24px';

            return buildRenderItems(order.lines).map(item => {
              if (item.type === 'product-header') {
                return (
                  <div key={`ph-${item.product.id}`} style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '7px 16px 5px',
                    borderTop: `1px solid rgba(255,255,255,0.06)`,
                    background: 'rgba(20,184,166,0.05)',
                  }}>
                    <i className="fa-solid fa-layer-group" style={{ color: TEAL, fontSize: '10px' }} />
                    <span style={{ color: TEAL, fontWeight: '700', fontSize: '11px' }}>
                      {item.product.name}
                    </span>
                  </div>
                );
              }

              const { line, seq, isChild } = item;
              const isEdit    = editingLine?.id === line.id;
              const isEDspch  = editingLineDispatch === line.id;
              const lDispatch = line.dispatch_date;
              const current   = line.current_qty || 0;
              // Made and from stock together cover the line (2 Oct 2026).
              const fromStock = line.from_stock_qty || 0;
              const covered   = current + fromStock;
              const isDone    = covered >= (line.quantity || 1) && (line.quantity || 0) > 0;
              const isPartial = covered > 0 && !isDone;
              // What the board holds for this line (board_days from the server since
              // 2 Oct 2026; the old mark until then).
              const onBoard     = line.board_days == null ? !!line.scheduled : daysOnBoard(line) > 0;
              const allDaysDone = line.board_days > 0 && line.done_days === line.board_days;

              const canMarkStock = !readOnly && onLineStock && !isDone
                && ((!onBoard && line.scheduled) || allDaysDone);
              return (
                <React.Fragment key={line.id}>
                <div style={{
                  display: 'grid', gridTemplateColumns: C, columnGap: '8px',
                  padding: isChild ? '5px 16px' : '7px 16px', fontSize: '12px',
                  borderTop: `1px solid rgba(255,255,255,0.06)`,
                  alignItems: 'center',
                  // A part sold as it is stands at the level of a product, not
                  // among a product's parts (the director, 2 Nov 2026: "they get
                  // lost among the parts that belong to products").
                  ...(isChild ? {
                    borderLeft: '3px solid rgba(20,184,166,0.25)',
                    background: 'rgba(20,184,166,0.02)',
                  } : {
                    background: 'rgba(20,184,166,0.05)',
                  }),
                }}>

                  {/* 1: seq */}
                  <div style={{ color: MUTED, fontSize: '10px', ...(isChild ? { paddingLeft: '10px' } : {}) }}>{seq}</div>

                  {/* 2: part number */}
                  <div style={{ color: MUTED, fontSize: '11px', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(isChild ? { paddingLeft: '10px' } : {}) }}>
                    {line.part?.part_number || line.part_number || '—'}
                  </div>

                  {/* 3: name -- bold, with its own mark, for a part sold as it is */}
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(isChild ? { paddingLeft: '10px' } : {}) }}>
                    {!isChild && <i className="fa-solid fa-cube" title="Sold as a part" style={{ color: TEAL, fontSize: '10px', marginRight: '6px' }} />}
                    <span
                      onClick={line.part_id ? () => window.open(`/admin/parts/${line.part_id}`, '_blank') : undefined}
                      title={line.part?.name || line.part_number}
                      style={{ cursor: line.part_id ? 'pointer' : 'default', color: TEXT,
                        ...(isChild ? {} : { fontWeight: '700', fontSize: '13px' }) }}>
                      {line.part?.name || line.part_number || `Part #${line.part_id}`}
                    </span>
                    {line.new_since_revert && (
                      <NewBadge what="Added" onDismiss={onDismissNew ? () => onDismissNew({ lines: [line.id] }) : undefined} />
                    )}
                    {line.schedule_warning && (
                      <i className="fa-solid fa-triangle-exclamation"
                        title={line.schedule_warning}
                        style={{ color: '#f59e0b', marginLeft: '5px', fontSize: '10px' }} />
                    )}
                  </div>

                  {/* 4: ordered qty — becomes input when editing */}
                  <div style={{ textAlign: 'center', color: MUTED }}>
                    {isEdit ? (
                      <input type="number" min="1" value={editingLine.qty}
                        onChange={e => setEditingLine(s => ({ ...s, qty: e.target.value }))}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveEdit(line.id);
                          if (e.key === 'Escape') setEditingLine(null);
                        }}
                        style={{ width: '60px', background: '#161b22', border: `1px solid ${BORDER}`,
                          borderRadius: '5px', color: TEXT, padding: '2px 6px', fontSize: '12px', textAlign: 'right' }}
                        autoFocus />
                    ) : (line.quantity || 0).toLocaleString()}
                  </div>

                  {/* 5: made so far, and what came from stock */}
                  <div style={{ textAlign: 'center',
                    color: isDone ? '#4ade80' : isPartial ? '#f59e0b' : MUTED }}>
                    {!isEdit && (current > 0 ? current.toLocaleString() : '—')}
                    {!isEdit && fromStock > 0 && (
                      <div onClick={!readOnly && onLineStock ? () => openStock(line, covered) : undefined}
                        title={!readOnly && onLineStock ? 'Supplied from stock — click to change' : 'Supplied from stock'}
                        style={{ fontSize: '10px', color: '#93c5fd', cursor: !readOnly && onLineStock ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                        + {fromStock.toLocaleString()} stock
                      </div>
                    )}
                  </div>

                  {/* 6: deadline */}
                  <div style={{ textAlign: 'center' }}>
                    {!isEdit && <DeadlineCell week={order.delivery_week} year={order.delivery_year} deliveryDate={order.delivery_date} />}
                  </div>

                  {/* 7: schedule status -- from the board itself. A line taken off the
                      board keeps its "scheduled" mark, so the mark alone said
                      "Scheduled" for lines nothing was planned for any more (2 Oct 2026). */}
                  <div style={{ textAlign: 'center' }}>
                    {!isEdit && statusLink(onBoard
                      ? (allDaysDone
                          // Every day it had on the board has run: no longer 'Scheduled' (2 Oct 2026).
                          ? <Badge colour={MUTED}>Produced</Badge>
                          : <Badge colour={MUTED}>Scheduled</Badge>)
                      : fromStock > 0
                        ? <Badge colour="#93c5fd">From stock</Badge>
                        : line.scheduled
                          ? <span title="Its days were taken off the board, so nothing is planned for this line now — for example when it was supplied from stock."><Badge colour={MUTED}>Off the board</Badge></span>
                          : <Badge colour={MUTED}>Pending</Badge>,
                      line, covered, isDone
                    )}
                  </div>

                  {/* 8: production status badge */}
                  <div style={{ textAlign: 'center' }}>
                    {!isEdit && canMarkStock && (
                      <button onClick={() => openStock(line, covered)}
                        title="Say how many pieces of this line were supplied from stock"
                        style={{ background: 'transparent', border: '1px dashed #93c5fd88', color: '#93c5fd', borderRadius: '6px',
                          padding: '2px 6px', fontSize: '9px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
                          marginRight: allDaysDone ? '4px' : 0 }}>
                        From stock…
                      </button>
                    )}
                    {!isEdit && (onBoard || isDone) && statusLink(
                      isDone
                        ? <Badge colour="#4ade80" solid>Completed</Badge>
                        : allDaysDone
                          ? <span title={`Every day on the board is done: ${current.toLocaleString()} made of ${(line.quantity || 0).toLocaleString()}.`}><Badge colour="#f59e0b">Finished short</Badge></span>
                          : isPartial || line.done_days > 0
                            ? <Badge colour="#f59e0b">In progress</Badge>
                            : <Badge colour={MUTED}>Scheduled</Badge>,
                      line, covered, isDone
                    )}
                  </div>

                  {/* 9: dispatch date */}
                  <div style={{ position: 'relative' }}>
                    {!readOnly && !isEdit && (
                      isEDspch ? (
                        <input type="date"
                          defaultValue={lDispatch || order.dispatch_date || order.suggested_dispatch_date || ''}
                          autoFocus
                          onBlur={e => {
                            setEditingLineDispatch(null);
                            if (e.target.value !== lDispatch) onLineDispatch(line.id, e.target.value || null);
                          }}
                          onKeyDown={e => { if (e.key === 'Escape') setEditingLineDispatch(null); }}
                          style={{ position: 'absolute', right: 0, zIndex: 10, background: '#161b22',
                            border: `1px solid ${TEAL}`, borderRadius: '5px', color: TEXT,
                            padding: '2px 4px', fontSize: '10px', outline: 'none', width: '110px' }}
                        />
                      ) : (
                        <button onClick={() => setEditingLineDispatch(line.id)}
                          title={lDispatch
                            ? `Dispatch: ${fmtDate(lDispatch)} — click to change`
                            : 'Set dispatch date for this line'}
                          style={{ background: 'none', width: '100%',
                            border: `1px ${lDispatch ? 'solid' : 'dashed'} ${lDispatch ? 'rgba(20,184,166,0.55)' : BORDER}`,
                            color: lDispatch ? TEAL : MUTED,
                            borderRadius: '4px', padding: '2px 5px', fontSize: '10px', cursor: 'pointer' }}>
                          <i className="fa-solid fa-truck" />
                        </button>
                      )
                    )}
                  </div>

                  {/* 10: edit-qty / confirm */}
                  <div>
                    {!readOnly && (
                      isEdit ? (
                        <button onClick={() => saveEdit(line.id)}
                          style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '4px',
                            padding: '2px 6px', fontSize: '10px', cursor: 'pointer', width: '100%' }}>
                          ✓
                        </button>
                      ) : !editingLine ? (
                        <button onClick={() => setEditingLine({ id: line.id, qty: line.quantity })}
                          title="Edit quantity"
                          style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED,
                            borderRadius: '4px', padding: '2px 5px', fontSize: '10px', cursor: 'pointer', width: '100%' }}>
                          <i className="fa-solid fa-pen" />
                        </button>
                      ) : null
                    )}
                  </div>

                  {/* 11: delete / cancel-edit */}
                  <div>
                    {!readOnly && (
                      isEdit ? (
                        <button onClick={() => setEditingLine(null)}
                          style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED,
                            borderRadius: '4px', padding: '2px 4px', fontSize: '10px', cursor: 'pointer', width: '100%' }}>
                          ✕
                        </button>
                      ) : (
                        <button onClick={() => onDeleteLine([line.id])}
                          title="Delete line"
                          style={{ background: 'none', border: 'none', color: '#ef4444',
                            padding: '2px 3px', fontSize: '11px', cursor: 'pointer' }}>
                          <i className="fa-regular fa-trash-can" />
                        </button>
                      )
                    )}
                  </div>
                  {/* 12: empty chevron-alignment column */}
                  <div />
                </div>
                </React.Fragment>
              );
            });
          })()}

          {order.notes && (
            <div style={{ padding: '8px 16px 12px', fontSize: '11px', color: MUTED,
              borderTop: `1px solid rgba(255,255,255,0.06)` }}>
              <i className="fa-solid fa-note-sticky" style={{ marginRight: '5px', fontSize: '10px' }} />
              {order.notes}
            </div>
          )}
          {canSeeHistory && (
            <div style={{ padding: '8px 16px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button onClick={() => navigate(`/admin/history?type=order&id=${order.id}`)}
                style={{ background: 'none', border: 'none', padding: 0, color: TEAL, fontSize: '11px', cursor: 'pointer' }}>
                <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: '5px', fontSize: '10px' }} />
                History of this order — its runs, what moved and why
              </button>
            </div>
          )}
          </div>{/* end minWidth scroll inner */}
        </div>
      )}
    </div>
  );
}

// ─── Reschedule impact modal ─────────────────────────────────────────────────

function RescheduleImpactModal({ impact, onConfirm, onBack, saving }) {
  const hasBreaches = impact.some(ri => ri.hasDeadlineBreach);

  function statusColor(days) {
    if (days === null) return MUTED;
    if (days < 0)  return '#ef4444';
    if (days <= 3) return '#f59e0b';
    return '#4ade80';
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '16px',
        width: '100%', maxWidth: '560px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: TEXT }}>Reschedule Impact</div>
          <div style={{ fontSize: '12px', color: MUTED, marginTop: '3px' }}>
            Saving this edit will adjust the schedule on the following jobs.
          </div>
          {hasBreaches && (
            <div style={{ marginTop: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#fca5a5', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ flexShrink: 0, marginTop: '1px' }} />
              One or more jobs will be pushed past their delivery deadline. You can still proceed, but you may want to investigate.
            </div>
          )}
        </div>

        <div style={{ overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {impact.map((ri, i) => (
            <div key={i}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, marginBottom: '8px' }}>
                {ri.partName} — {ri.type === 'grow' ? `+${ri.delta} day${ri.delta !== 1 ? 's' : ''}` : `${ri.delta} day${Math.abs(ri.delta) !== 1 ? 's' : ''}`}
              </div>

              {ri.pushedPlans.length === 0 ? (
                <div style={{ fontSize: '12px', color: MUTED, fontStyle: 'italic' }}>
                  {ri.type === 'grow' ? 'No other jobs after this one on the same machine.' : 'No subsequent jobs to pull forward.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {ri.pushedPlans.map((p, j) => {
                    const color = statusColor(p.daysBeforeDeadline);
                    return (
                      <div key={j} style={{ background: '#0f1117', borderRadius: '8px', padding: '10px 12px',
                        border: `1px solid ${p.daysBeforeDeadline !== null && p.daysBeforeDeadline < 0 ? 'rgba(239,68,68,0.35)' : '#2a3040'}`,
                        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '140px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: TEXT }}>{p.partName}</div>
                          <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>
                            {p.dayCount} day{p.dayCount !== 1 ? 's' : ''} · ends {p.oldLastDate} → <strong style={{ color: TEXT }}>{p.newLastDate}</strong>
                          </div>
                        </div>
                        {p.deadline && (
                          <div style={{ fontSize: '11px', fontWeight: '600', color, whiteSpace: 'nowrap' }}>
                            {p.daysBeforeDeadline < 0
                              ? <><i className="fa-solid fa-circle-exclamation" style={{ marginRight: '4px' }} />{Math.abs(p.daysBeforeDeadline)}d after deadline</>
                              : p.daysBeforeDeadline === 0
                                ? <><i className="fa-solid fa-circle-exclamation" style={{ marginRight: '4px' }} />On deadline</>
                                : <><i className="fa-solid fa-circle-check" style={{ marginRight: '4px' }} />{p.daysBeforeDeadline}d before deadline</>
                            }
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ padding: '16px 20px', borderTop: '1px solid #2a3040', display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
          <button onClick={onBack} disabled={saving}
            style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: '8px', color: MUTED,
              padding: '8px 18px', cursor: 'pointer', fontSize: '13px', fontFamily: 'system-ui' }}>
            ← Go back
          </button>
          <button onClick={onConfirm} disabled={saving}
            style={{ background: saving ? 'rgba(20,184,166,0.3)' : 'rgba(20,184,166,0.15)', border: `1px solid ${saving ? 'transparent' : 'rgba(20,184,166,0.5)'}`,
              borderRadius: '8px', color: TEAL, padding: '8px 22px', cursor: saving ? 'default' : 'pointer',
              fontSize: '13px', fontWeight: '600', fontFamily: 'system-ui' }}>
            {saving ? 'Saving…' : 'Save & Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit order modal ─────────────────────────────────────────────────────────

function EditOrderModal({ order, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    customer:      order.customer      || '',
    order_number:  order.order_number  || '',
    date_received: order.date_received || '',
    delivery_week: order.delivery_week || '',
    delivery_year: order.delivery_year || '',
    delivery_date: order.delivery_date || '',
    schedule_intent: order.schedule_intent || 'asap',
    notes:         order.notes         || '',
  });

  // lines state: existing lines with _deleted flag + _origQty for change detection
  const [lines, setLines] = useState(
    (order.lines || []).map(l => ({ ...l, _deleted: false, _origQty: l.quantity }))
  );
  const [newLines, setNewLines]         = useState([{ item_key: '', quantity: '', unit_price: '' }]);
  const { parts, products }             = usePartsProducts();
  const prices                          = usePriceList();
  const [saving, setSaving]             = useState(false);
  const [reschedulePreview, setReschedulePreview] = useState(null);

  function setLineField(id, field, val) {
    setLines(ls => ls.map(l => l.id === id ? { ...l, [field]: val } : l));
  }
  // A product's price is one price for the product: shown on its first line,
  // and a special price typed there holds for every line of it.
  const listOf = (l) => prices[l.product_id ? `product:${l.product_id}` : `part:${l.part_id}`];
  const firstOfProduct = {};
  lines.filter(l => !l._deleted).forEach(l => { if (l.product_id && !(l.product_id in firstOfProduct)) firstOfProduct[l.product_id] = l.id; });
  function setLinePrice(l, v) {
    setLines(ls => ls.map(x => ((l.product_id ? x.product_id === l.product_id : x.id === l.id) ? { ...x, unit_price: v } : x)));
  }

  function toggleDelete(id) {
    setLines(ls => ls.map(l => l.id === id ? { ...l, _deleted: !l._deleted } : l));
  }

  function setNewLine(i, field, val) {
    setNewLines(prev => {
      const next = prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l);
      if (field === 'item_key' && val && i === prev.length - 1) {
        return [...next, { item_key: '', quantity: '', unit_price: '' }];
      }
      return next;
    });
  }

  function buildBody(previewFlag) {
    const lineChanges = [
      ...lines.filter(l => l._deleted).map(l => ({ id: l.id, _delete: true })),
      ...lines.filter(l => !l._deleted).map(l => ({
        id: l.id,
        quantity: parseInt(l.quantity),
        unit_price: linePrice(l.unit_price, listOf(l)),
      })),
      ...newLines.filter(l => l.item_key && l.quantity).map(l => {
        const qty = parseInt(l.quantity);
        const price = linePrice(l.unit_price, prices[priceKeyOf(l.item_key)]);
        if (l.item_key.startsWith('product:')) {
          return { product_id: l.item_key.slice(8), quantity: qty, unit_price: price };
        }
        const part = parts.find(p => String(p.id) === l.item_key.slice(5));
        return { part_id: parseInt(l.item_key.slice(5)), part_number: part?.part_number || '', quantity: qty, unit_price: price };
      }),
    ];
    return {
      ...form,
      delivery_week: form.delivery_week !== '' ? parseInt(form.delivery_week) : null,
      delivery_year: form.delivery_year !== '' ? parseInt(form.delivery_year) : null,
      lines: lineChanges,
      preview: previewFlag,
    };
  }

  async function handleSave() {
    setSaving(true);
    try {
      // First pass: preview to check for reschedule impact (no DB writes)
      const previewBody = buildBody(true);
      const { data: preview } = await api.post(`/api/v1/orders/${order.id}/edit`, previewBody);
      if (preview.needsReschedule) {
        setReschedulePreview({ impact: preview.impact, pendingBody: buildBody(false) });
        setSaving(false);
        return;
      }
      // No reschedule needed — commit all changes now
      const { data } = await api.post(`/api/v1/orders/${order.id}/edit`, buildBody(false));
      toast.success('Order updated');
      onSaved(data.order);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
      setSaving(false);
    }
  }

  async function handleConfirmReschedule() {
    setSaving(true);
    try {
      const { data } = await api.post(`/api/v1/orders/${order.id}/edit`, reschedulePreview.pendingBody);
      toast.success('Order updated and schedule adjusted');
      onSaved(data.order);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
      setSaving(false);
    }
  }

  const OV  = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 350,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
  const BOX = { background: '#1a2030', border: `1px solid ${BORDER}`, borderRadius: '16px',
    width: '100%', maxWidth: '640px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
  const FLD = { background: '#0f1117', border: `1px solid ${BORDER}`, borderRadius: '7px',
    color: TEXT, fontSize: '13px', padding: '7px 10px', fontFamily: 'system-ui', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const LBL = { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, marginBottom: '4px' };

  if (reschedulePreview) {
    return (
      <RescheduleImpactModal
        impact={reschedulePreview.impact}
        saving={saving}
        onBack={() => setReschedulePreview(null)}
        onConfirm={handleConfirmReschedule}
      />
    );
  }

  return (
    <div style={OV} onClick={onClose}>
      <div style={BOX} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: TEXT }}>Edit Order</div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
              {order.customer || 'Order'}{order.order_number ? ` #${order.order_number}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: '16px', padding: '0 0 0 8px' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Order fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <div style={LBL}>Customer</div>
              <input style={FLD} value={form.customer} onChange={e => setForm(f => ({ ...f, customer: e.target.value }))} placeholder="Customer name" />
            </div>
            <div>
              <div style={LBL}>Order number</div>
              <input style={FLD} value={form.order_number} onChange={e => setForm(f => ({ ...f, order_number: e.target.value }))} placeholder="e.g. PO-1234" />
            </div>
            <div>
              <div style={LBL}>Date received</div>
              <input type="date" style={FLD} value={form.date_received} onChange={e => setForm(f => ({ ...f, date_received: e.target.value }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <div style={LBL}>Delivery week</div>
                <input type="number" min="1" max="53" style={FLD} value={form.delivery_week} onChange={e => setForm(f => ({ ...f, delivery_week: e.target.value }))} placeholder="Wk" />
              </div>
              <div>
                <div style={LBL}>Year</div>
                <input type="number" min="2020" max="2099" style={FLD} value={form.delivery_year} onChange={e => setForm(f => ({ ...f, delivery_year: e.target.value }))} placeholder="YYYY" />
              </div>
            </div>
            <div>
              <div style={LBL}>Specific deadline date <span style={{ fontWeight: '400', color: MUTED }}>(optional)</span></div>
              <input type="date" style={FLD} value={form.delivery_date || ''}
                onChange={e => {
                  const d = e.target.value;
                  if (d) {
                    const parsed = new Date(d + 'T12:00:00Z');
                    setForm(f => ({ ...f, delivery_date: d, delivery_week: isoWeek(parsed), delivery_year: parsed.getUTCFullYear() }));
                  } else {
                    setForm(f => ({ ...f, delivery_date: '' }));
                  }
                }} />
            </div>
            {!form.delivery_week && !form.delivery_date && (
              <div style={{ gridColumn: '1 / -1' }}>
                <ScheduleIntentField
                  value={form.schedule_intent}
                  onChange={v => setForm(f => ({ ...f, schedule_intent: v }))}
                />
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={LBL}>Notes</div>
              <textarea style={{ ...FLD, resize: 'vertical', minHeight: '56px' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes…" />
            </div>
          </div>

          {/* Lines */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: TEXT, marginBottom: '10px' }}>Order lines</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {/* Existing lines */}
              {lines.map(l => {
                const qtyChanged = !l._deleted && parseInt(l.quantity) !== l._origQty;
                return (
                  <div key={l.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 90px 116px 32px',
                    gap: '8px', alignItems: 'center', padding: '8px 10px',
                    background: l._deleted ? 'rgba(239,68,68,0.06)' : '#0f1117',
                    borderRadius: '8px', border: `1px solid ${l._deleted ? 'rgba(239,68,68,0.3)' : BORDER}`,
                    opacity: l._deleted ? 0.5 : 1,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      <span style={{ fontSize: '12px', color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.part?.name || l.part_number || `Part #${l.part_id}`}
                      </span>
                      {l.scheduled && (
                        <span style={{ fontSize: '10px', fontWeight: '700', color: TEAL, background: 'rgba(20,184,166,0.1)',
                          border: '1px solid rgba(20,184,166,0.3)', borderRadius: '4px', padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          Scheduled
                        </span>
                      )}
                      {qtyChanged && l.scheduled && (
                        <span title="Changing quantity on a scheduled line will adjust the schedule" style={{ color: '#f59e0b', flexShrink: 0 }}>
                          <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '11px' }} />
                        </span>
                      )}
                    </div>
                    <input
                      type="number" min="1"
                      disabled={l._deleted}
                      value={l.quantity}
                      onChange={e => setLineField(l.id, 'quantity', e.target.value)}
                      style={{ ...FLD, textAlign: 'right', padding: '5px 8px', opacity: l._deleted ? 0.4 : 1 }}
                      placeholder="Qty"
                    />
                    {l.product_id && firstOfProduct[l.product_id] !== l.id && !l._deleted
                      ? <span style={{ fontSize: '10.5px', color: MUTED, textAlign: 'right' }} title="The product's price is on its first line">in the product</span>
                      : <PriceBox value={l.unit_price} list={listOf(l)} disabled={l._deleted} onChange={v => setLinePrice(l, v)} style={{ opacity: l._deleted ? 0.4 : 1 }} />}
                    <button
                      onClick={() => toggleDelete(l.id)}
                      title={l._deleted ? 'Undo remove' : 'Remove line'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: l._deleted ? '#4ade80' : '#ef4444',
                        fontSize: '13px', padding: '4px', lineHeight: 1 }}>
                      <i className={l._deleted ? 'fa-solid fa-rotate-left' : 'fa-regular fa-trash-can'} />
                    </button>
                  </div>
                );
              })}

              {/* Header labels for new lines */}
              {newLines.some(l => l.part_id) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 116px 32px', gap: '8px', padding: '6px 10px 0' }}>
                  <div style={{ fontSize: '10px', color: MUTED }}>Part</div>
                  <div style={{ fontSize: '10px', color: MUTED, textAlign: 'right' }}>Quantity</div>
                  <div style={{ fontSize: '10px', color: MUTED, textAlign: 'right' }}>Price</div>
                  <div />
                </div>
              )}

              {/* New lines */}
              {newLines.map((nl, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 116px 32px', gap: '8px', alignItems: 'center',
                  padding: '8px 10px', background: 'rgba(20,184,166,0.04)', borderRadius: '8px', border: `1px dashed rgba(20,184,166,0.25)` }}>
                  <select
                    value={nl.item_key}
                    onChange={e => setNewLine(i, 'item_key', e.target.value)}
                    style={{ ...FLD, padding: '5px 8px' }}>
                    <option value="">+ Add part or product…</option>
                    {products.length > 0 && (
                      <optgroup label="── Products ──">
                        {products.map(prod => (
                          <option key={`product:${prod.id}`} value={`product:${prod.id}`} style={{ color: TEAL }}>
                            {prod.name || prod.code}{prod.name && prod.code ? ` (${prod.code})` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="── Parts ──">
                      {parts.map(p => <option key={`part:${p.id}`} value={`part:${p.id}`}>{p.name} ({p.part_number})</option>)}
                    </optgroup>
                  </select>
                  <input type="number" min="1" value={nl.quantity}
                    onChange={e => setNewLine(i, 'quantity', e.target.value)}
                    style={{ ...FLD, textAlign: 'right', padding: '5px 8px' }} placeholder="Qty"
                    disabled={!nl.item_key} />
                  <PriceBox value={nl.unit_price} list={prices[priceKeyOf(nl.item_key)]} disabled={!nl.item_key}
                    onChange={v => setNewLine(i, 'unit_price', v ?? '')} />
                  <button
                    onClick={() => setNewLines(nl => nl.filter((_, idx) => idx !== i))}
                    disabled={newLines.length <= 1 && !nl.item_key}
                    style={{ background: 'none', border: 'none', cursor: nl.item_key ? 'pointer' : 'default',
                      color: nl.item_key ? '#ef4444' : BORDER, fontSize: '13px', padding: '4px', lineHeight: 1 }}>
                    <i className="fa-regular fa-trash-can" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: '8px',
            color: MUTED, padding: '8px 18px', cursor: 'pointer', fontSize: '13px', fontFamily: 'system-ui' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ background: saving ? 'rgba(20,184,166,0.3)' : 'rgba(20,184,166,0.15)', border: `1px solid rgba(20,184,166,0.5)`,
              borderRadius: '8px', color: TEAL, padding: '8px 22px', cursor: saving ? 'default' : 'pointer',
              fontSize: '13px', fontWeight: '600', fontFamily: 'system-ui' }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Fulfill modal ───────────────────────────────────────────────────────────

function FulfillModal({ data, onConfirm, onCancel, confirming }) {
  const { hasShortfall, hasRunning, lines = [] } = data;
  const noTracking = lines.every(l => l.good_parts === 0 && !l.from_stock);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={onCancel}>
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '14px',
        maxWidth: '520px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>
            <i className="fa-solid fa-circle-check" style={{ color: '#4ade80', marginRight: '8px' }} />
            Mark Order as Complete
          </div>
          <div style={{ fontSize: '12px', color: MUTED }}>
            {noTracking
              ? 'Production tracking not yet enabled. Mark this order as shipped and close it.'
              : 'Confirm production is complete and this order is ready to ship.'}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {noTracking ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {lines.map(l => (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', background: '#161b22', borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px', color: TEXT }}>
                    {l.part_name}
                    {l.part_number && <span style={{ color: MUTED, marginLeft: '6px', fontSize: '11px' }}>{l.part_number}</span>}
                  </span>
                  <span style={{ fontSize: '12px', color: MUTED }}>{l.quantity.toLocaleString()} pcs</span>
                </div>
              ))}
            </div>
          ) : (
            <>
              {hasRunning && (
                <div style={{ background: '#1a1200', border: '1px solid #f59e0b44', borderRadius: '8px',
                  padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#fbbf24',
                  display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ marginTop: '1px', flexShrink: 0 }} />
                  Some jobs are still running. Completing now will record current counts only.
                </div>
              )}
              {hasShortfall && (
                <div style={{ background: '#1a0a0a', border: '1px solid #ef444444', borderRadius: '8px',
                  padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#f87171',
                  display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <i className="fa-solid fa-circle-exclamation" style={{ marginTop: '1px', flexShrink: 0 }} />
                  One or more lines are below target quantity. Director can still confirm fulfillment.
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 14px', alignItems: 'center' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Part</div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Target</div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Made</div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Gap</div>
                {lines.map(l => (
                  <React.Fragment key={l.id}>
                    <div style={{ fontSize: '13px', color: TEXT, paddingTop: '8px', borderTop: `1px solid ${BORDER}22` }}>
                      {l.part_name}
                      {l.part_number && <span style={{ color: MUTED, marginLeft: '6px', fontSize: '11px' }}>{l.part_number}</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: MUTED, textAlign: 'right', paddingTop: '8px', borderTop: `1px solid ${BORDER}22` }}>
                      {l.quantity.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '12px', color: l.is_complete ? '#4ade80' : '#f59e0b',
                      textAlign: 'right', fontWeight: '600', paddingTop: '8px', borderTop: `1px solid ${BORDER}22` }}>
                      {l.good_parts.toLocaleString()}
                      {l.from_stock > 0 && <div style={{ fontSize: '10px', color: '#93c5fd', fontWeight: 400 }}>+ {l.from_stock.toLocaleString()} stock</div>}
                    </div>
                    <div style={{ fontSize: '12px', color: l.shortfall > 0 ? '#f87171' : '#4ade80',
                      textAlign: 'right', paddingTop: '8px', borderTop: `1px solid ${BORDER}22` }}>
                      {l.shortfall > 0 ? `-${l.shortfall.toLocaleString()}` : '✓'}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${BORDER}`,
          display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={onCancel} disabled={confirming}
            style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED,
              borderRadius: '8px', padding: '8px 20px', fontSize: '13px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={confirming}
            style={{ background: '#16a34a', border: 'none', color: '#fff',
              borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: '600',
              cursor: confirming ? 'wait' : 'pointer', opacity: confirming ? 0.7 : 1 }}>
            {confirming ? 'Saving…' : 'Confirm Complete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Orders({ readOnly = false }) {
  const goBack   = useBack('/admin');
  const [searchParams] = useSearchParams();
  const { isDirector } = useAuth();
  const { toast, confirm } = useToast();
  // The History tab became the completed orders under the active ones (2 Oct
  // 2026); an old ?tab=history link lands on the list.
  const [tab, setTab] = useState(searchParams.get('tab') === 'history' ? 'list' : (searchParams.get('tab') || 'list')); // 'list' | 'manual' | 'excel'
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const ordersLoaded = useRef(false);   // after the first load, refreshes stay quiet
  const [scheduleState, setScheduleState] = useState(null); // { order, proposalResult }
  const [proposing, setProposing] = useState(false);
  const [historyOrders, setHistoryOrders] = useState([]);
  const historyLoaded = useRef(false);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  // The invoices issued from the app, by order, so an order's Invoice button
  // opens the one it already has (directors only, as the button).
  const [invoicesByOrder, setInvoicesByOrder] = useState({});
  useEffect(() => {
    if (readOnly || !isDirector()) return;
    api.get('/api/v1/invoices').then(r => {
      const by = {};
      for (const inv of r.data.invoices || []) {
        if (inv.order_id && inv.source !== 'outside') (by[inv.order_id] = by[inv.order_id] || []).push(inv);
      }
      for (const k of Object.keys(by)) by[k].sort((a, b) => a.seq - b.seq);
      setInvoicesByOrder(by);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // A link names an order (?order=ID, e.g. from the at-risk list): bring it
  // into view once, and outline it for a moment so the eye lands on it.
  const focusedOrder = useRef(false);
  const linkedOrder = searchParams.get('order');
  useEffect(() => {
    const id = linkedOrder;
    if (!id || focusedOrder.current || !orders.length) return;
    // A completed order beyond the ten shown: show them all.
    const doneIds = [...historyOrders].sort((a, b) => String(b.fulfilled_at || '').localeCompare(String(a.fulfilled_at || ''))).map(o => String(o.id));
    if (doneIds.indexOf(String(id)) >= 10 && !showAllCompleted) { setShowAllCompleted(true); return; }
    const el = document.getElementById(`order-${id}`);
    if (!el) return;
    focusedOrder.current = true;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.style.outline = '2px solid #14b8a6';
    setTimeout(() => { el.style.outline = ''; }, 2500);
  }, [orders, historyOrders, showAllCompleted, linkedOrder]); // eslint-disable-line react-hooks/exhaustive-deps
  const [fulfillModal, setFulfillModal] = useState(null); // { orderId, data } | null
  const [fulfillConfirming, setFulfillConfirming] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null); // order object | null
  const [sortBy, setSortBy] = useState('deadline');
  const [showCapacity, setShowCapacity] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  // Once, when the page opens (it loads the completed orders too).
  useEffect(() => {
    loadOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Only the first load shows "loading". A refresh after a change keeps the
  // list on screen, so an order that was open stays open (2 Oct 2026: marking
  // each line from stock closed the order every time).
  async function loadOrders() {
    if (!ordersLoaded.current) setLoading(true);
    try {
      const { data } = await api.get('/api/v1/orders');
      setOrders(Array.isArray(data) ? data : []);
      ordersLoaded.current = true;
      // Completed orders live under the active ones, and a change to an active
      // one can complete it -- so both lists refresh together.
      loadHistory();
    } catch {
      if (!ordersLoaded.current) setOrders([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleDismissNew(marks) {
    try {
      await api.post('/api/v1/history/new-marks/dismiss', marks);
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not dismiss the NEW label');
    }
  }

  async function loadHistory() {
    try {
      const { data } = await api.get('/api/v1/orders?status=fulfilled');
      setHistoryOrders(Array.isArray(data) ? data : []);
      historyLoaded.current = true;
    } catch {
      if (!historyLoaded.current) setHistoryOrders([]);
    }
  }

  async function handleFulfillClick(orderId) {
    try {
      const { data } = await api.get(`/api/v1/orders/${orderId}/completion`);
      setFulfillModal({ orderId, data });
    } catch (err) {
      toast.error('Could not load completion data: ' + (err.response?.data?.error || err.message));
    }
  }

  async function handleFulfillConfirm() {
    if (!fulfillModal) return;
    setFulfillConfirming(true);
    try {
      await api.post(`/api/v1/orders/${fulfillModal.orderId}/fulfill`);
      setFulfillModal(null);
      toast.success('Order marked as complete.');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setFulfillConfirming(false);
    }
  }

  async function handleReopen(orderId) {
    try {
      await api.post(`/api/v1/orders/${orderId}/reopen`);
      toast.success('Order reopened.');
      loadHistory();
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  }

  async function proposeSchedule(lineIds, prefs = {}, forcePrimaryPartIds = [], perPartBuffer = {}, crossOrderBatch = {}, importedOrders = []) {
    if (!lineIds || lineIds.length === 0) return;
    setProposing(true);
    try {
      const { data } = await api.post('/api/v1/orders/schedule/propose', {
        order_line_ids: lineIds,
        multi_shift: prefs.multi_shift || false,
        limit_mould_changes: prefs.limit_mould_changes || false,
        force_primary_for: forcePrimaryPartIds,
        floor_cycle_for: prefs.floor_cycle_for || [],
        schedule_mode: prefs.schedule_mode || 'asap',
        buffer_days: prefs.schedule_mode === 'buffer' ? (prefs.buffer_days || 2) : 0,
        per_part_buffer: perPartBuffer,
        cross_order_batch: crossOrderBatch,
      });
      const batchOpps = data.batch_opportunities || [];
      const pendingBatch = batchOpps.length > 0 && Object.keys(crossOrderBatch).length === 0;
      const pendingNaming = importedOrders.length > 0;
      setScheduleState({ proposalResult: data, lineIds, prefs, pendingBatch, pendingNaming, importedOrders, importedOrderIds: importedOrders.map(o => o.id) });
    } catch (err) {
      toast.error('Scheduling error: ' + (err.response?.data?.error || err.message));
    } finally {
      setProposing(false);
    }
  }

  function handleRepropose(forcePrimaryPartIds, perPartBuffer = {}, floorCycleFor = null) {
    if (!scheduleState) return;
    // The floor's cycle choice lives with the plan's preferences, so every re-run keeps it.
    const prefs = floorCycleFor ? { ...scheduleState.prefs, floor_cycle_for: floorCycleFor } : scheduleState.prefs;
    proposeSchedule(scheduleState.lineIds, prefs, forcePrimaryPartIds, perPartBuffer, scheduleState.crossOrderBatch || {});
  }

  function handleBatchDecide(decisions) {
    if (!scheduleState) return;
    const allDecisions = { ...(scheduleState.crossOrderBatch || {}), ...decisions };
    setScheduleState(s => ({ ...s, crossOrderBatch: allDecisions }));
    proposeSchedule(scheduleState.lineIds, scheduleState.prefs, [], {}, allDecisions);
  }

  async function handleScheduleOrder(order) {
    const unscheduled = (order.lines || []).filter(needsPlan).map(l => l.id);
    await proposeSchedule(unscheduled);
  }

  async function handleCommit(entries) {
    try {
      const warnings = scheduleState?.proposalResult?.warnings || [];
      const { data } = await api.post('/api/v1/orders/schedule/commit', { entries, warnings });
      setScheduleState(null);
      toast.success(`${data.inserted?.length || 0} job entries added to schedule.`);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  }

  function handleDeleteOrder(id) {
    confirm({
      message: 'Delete this order, all its lines, and all corresponding scheduled jobs?',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/api/v1/orders/${id}`);
          loadOrders();
        } catch (err) {
          toast.error(err.response?.data?.error || err.message);
        }
      },
    });
  }

  function handleDeleteLine(lineIdOrIds) {
    const ids = Array.isArray(lineIdOrIds) ? lineIdOrIds : [lineIdOrIds];
    confirm({
      message: ids.length > 1
        ? `Remove all ${ids.length} lines for this part and cancel their scheduled jobs?`
        : 'Remove this line and all its scheduled jobs from the schedule?',
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          for (const lineId of ids) {
            await api.delete(`/api/v1/orders/lines/${lineId}`);
          }
          loadOrders();
        } catch (err) {
          toast.error(err.response?.data?.error || err.message);
        }
      },
    });
  }

  async function handleDispatch(orderId, date) {
    try {
      await api.put(`/api/v1/orders/${orderId}`, { dispatch_date: date || null });
      loadOrders();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleLineDispatch(lineId, date) {
    try {
      await api.put(`/api/v1/orders/lines/${lineId}`, { dispatch_date: date || null });
      loadOrders();
    } catch (err) {
      toast.error(err.message);
    }
  }

  // Pieces of a line supplied from stock (2 Oct 2026); null clears it.
  async function handleLineStock(lineId, qty) {
    try {
      const { data } = await api.put(`/api/v1/orders/lines/${lineId}`, { from_stock_qty: qty === '' || qty == null ? null : Number(qty) });
      if (data?.order_completed) toast.success(`${data.order_completed} completed — every line is done. It is in History; Reopen it there if that was too soon.`, 8000);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  }

  async function handleEditLine(lineId, quantity) {
    try {
      const { data } = await api.put(`/api/v1/orders/lines/${lineId}`, { quantity });
      if (data?.order_completed) toast.success(`${data.order_completed} completed — every line is done. It is in History.`, 8000);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  }

  async function handleRename(orderId, { customer, order_number }) {
    try {
      await api.put(`/api/v1/orders/${orderId}`, { customer: customer || null, order_number: order_number || null });
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  }

  function handleEditOrder(order) {
    setEditingOrder(order);
  }

  function handleEditOrderSaved() {
    setEditingOrder(null);
    loadOrders();
  }

  function handleCancelImport() {
    const ids = scheduleState?.importedOrderIds || [];
    confirm({
      message: `Delete ${ids.length} imported order${ids.length !== 1 ? 's' : ''} and all their lines? This cannot be undone.`,
      confirmLabel: 'Delete Import',
      danger: true,
      onConfirm: async () => {
        try {
          for (const id of ids) await api.delete(`/api/v1/orders/${id}`);
          setScheduleState(null);
          loadOrders();
          toast.info('Import deleted.');
        } catch (err) {
          toast.error(err.response?.data?.error || err.message);
        }
      },
    });
  }

  function handleConfirmNoSchedule() {
    setScheduleState(null);
    loadOrders();
  }

  async function handleNamingComplete(names) {
    // names = { [orderId]: { customer, order_number } }
    try {
      await Promise.all(
        Object.entries(names).map(([id, v]) =>
          api.put(`/api/v1/orders/${id}`, { customer: v.customer || null, order_number: v.order_number || null })
        )
      );
      loadOrders();
    } catch (err) {
      toast.error('Could not save order names: ' + (err.response?.data?.error || err.message));
    }
    setScheduleState(s => ({ ...s, pendingNaming: false }));
  }

  function handleManualSaved(data, formPrefs) {
    setTab('list');
    loadOrders();
    const lineIds = (data.lines || []).map(l => l.id);
    proposeSchedule(lineIds, formPrefs);
  }

  function handleExcelSaved(result, formPrefs) {
    const { unknownParts, archivedParts, orders, message } = result;
    setTab('list');
    loadOrders();

    if (!orders || orders.length === 0) {
      toast.info(message || 'No new quantities detected — upload a file with changed quantities to import.');
      if (archivedParts && archivedParts.length > 0) {
        toast.warning(`Archived parts skipped: ${archivedParts.join(', ')} — reactivate them in Admin → Parts, then re-upload.`);
      }
      if (unknownParts && unknownParts.length > 0) {
        toast.warning(`Unknown part codes: ${unknownParts.join(', ')} — add them in Admin → Parts, then re-upload.`);
      }
      return;
    }

    if (archivedParts && archivedParts.length > 0) {
      toast.warning(`Archived parts skipped: ${archivedParts.join(', ')} — reactivate them in Admin → Parts to include in future imports.`);
    }
    if (unknownParts && unknownParts.length > 0) {
      toast.warning(`Unknown part codes: ${unknownParts.join(', ')} — add them in Admin → Parts and re-upload to pick up the delta.`);
    }
    const lineIds = (orders || []).flatMap(o => (o.lines || []).map(l => l.id));
    const importedOrders = (orders || []).map(o => ({
      id: o.order.id,
      delivery_week: o.order.delivery_week,
      delivery_year: o.order.delivery_year,
      customer: o.order.customer || '',
      order_number: o.order.order_number || '',
    })).sort((a, b) => a.delivery_year !== b.delivery_year ? a.delivery_year - b.delivery_year : a.delivery_week - b.delivery_week);
    proposeSchedule(lineIds, formPrefs, [], {}, {}, importedOrders);
  }

  const pastDueOrders = orders.filter(o => {
    if (!o.delivery_week || !o.delivery_year) return false;
    const y = currentYear(), w = currentWeek();
    return o.delivery_year < y || (o.delivery_year === y && o.delivery_week < w);
  });

  const sortedOrders = [...orders].sort((a, b) => {
    if (sortBy === 'deadline') {
      const av = (a.delivery_year || 9999) * 100 + (a.delivery_week || 99);
      const bv = (b.delivery_year || 9999) * 100 + (b.delivery_week || 99);
      return av - bv;
    }
    if (sortBy === 'customer') {
      return (a.customer || '').toLowerCase().localeCompare((b.customer || '').toLowerCase());
    }
    if (sortBy === 'name') {
      const an = `${a.customer || ''} ${a.order_number || ''}`.trim().toLowerCase();
      const bn = `${b.customer || ''} ${b.order_number || ''}`.trim().toLowerCase();
      return an.localeCompare(bn);
    }
    if (sortBy === 'size') {
      return (b.lines || []).length - (a.lines || []).length;
    }
    return 0;
  });

  const TABS = readOnly ? [] : [
    { key: 'list',    label: 'Orders',       icon: 'fa-solid fa-list' },
    { key: 'manual',  label: 'New Order',    icon: 'fa-solid fa-plus' },
    { key: 'excel',   label: 'Excel Import', icon: 'fa-solid fa-file-excel' },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: BG, minHeight: '100vh' }}>
      {!readOnly && (
        <div style={{ padding: '8px 0 0', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <button onClick={goBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
          </button>
          <span style={{ color: '#2a3040' }}>·</span>
          <span style={{ fontSize: '12px', color: TEXT }}>Orders</span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>Orders</h1>
          {!readOnly && <HistoryLink type="order" style={{ padding: '5px 11px', fontSize: '12px' }} />}
        </div>
        {/* Asking changes nothing, so a director has it on the Orders tab too (7 Oct 2026). */}
        {(!readOnly || isDirector()) && (
          <button onClick={() => setShowCapacity(true)}
            style={{ background: 'none', border: `1px solid ${BORDER}`, color: TEAL, borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px' }}
            onMouseEnter={e => { e.currentTarget.style.background = TEAL + '18'; e.currentTarget.style.borderColor = TEAL; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = BORDER; }}>
            <i className="fa-solid fa-clipboard-question" style={{ fontSize: '12px' }} />Can we take this order?
          </button>
        )}
      </div>
      <p style={{ color: MUTED, marginBottom: '20px', fontSize: '13px' }}>
        {readOnly ? 'Current customer orders and delivery status' : 'Customer orders and automatic scheduling'}
      </p>


      {/* Tab bar — hidden in read-only mode */}
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${BORDER}`, marginBottom: '24px' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ background: 'none', border: 'none', borderBottom: tab === t.key ? `2px solid ${TEAL}` : '2px solid transparent',
                color: tab === t.key ? TEXT : MUTED, padding: '10px 18px', fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className={t.icon} style={{ fontSize: '12px' }} />{t.label}
            </button>
          ))}
          {/* Sort control — right-aligned, only on list tab */}
          {tab === 'list' && !loading && orders.length > 0 && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', paddingBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: MUTED, fontWeight: '600',
                textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '2px' }}>Sort</span>
              {[
                { key: 'deadline', label: 'Deadline' },
                { key: 'customer', label: 'Customer' },
                { key: 'name',     label: 'Order #' },
                { key: 'size',     label: 'Size' },
              ].map(opt => (
                <button key={opt.key} onClick={() => setSortBy(opt.key)}
                  style={{
                    background: sortBy === opt.key ? TEAL + '22' : 'none',
                    border: `1px solid ${sortBy === opt.key ? TEAL : BORDER}`,
                    color: sortBy === opt.key ? TEAL : MUTED,
                    borderRadius: '6px', padding: '3px 10px', fontSize: '11px',
                    fontWeight: sortBy === opt.key ? '700' : '400',
                    cursor: 'pointer',
                  }}>
                  {opt.label}
                </button>
              ))}
              {isDirector() && (
                <button onClick={() => setShowCalendar(true)} title="Dispatch dates in your phone's calendar"
                  style={{ background: 'none', border: `1px solid ${BORDER}`, color: TEAL, borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', marginLeft: '8px' }}>
                  <i className="fa-solid fa-calendar-plus" style={{ marginRight: '5px' }} />Calendar
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {showCalendar && <CalendarLinkModal onClose={() => setShowCalendar(false)} />}

      {/* List (always visible; tabs only exist in admin mode) */}
      {(tab === 'list' || readOnly) && (
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {/* Auto-suggest fulfillment banner */}
          {!readOnly && pastDueOrders.length > 0 && (
            <div style={{ background: '#0d1a0d', border: '1px solid #16a34a44', borderRadius: '10px',
              padding: '12px 16px', marginBottom: '18px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fa-solid fa-circle-check" style={{ color: '#4ade80', fontSize: '14px' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#4ade80' }}>
                    {pastDueOrders.length} order{pastDueOrders.length !== 1 ? 's' : ''} past delivery week
                  </div>
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '1px' }}>
                    {pastDueOrders.map(o => `Wk ${o.delivery_week}/${o.delivery_year}${o.customer ? ` · ${o.customer}` : ''}`).join('  ·  ')}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {pastDueOrders.map(o => (
                  <button key={o.id} onClick={() => handleFulfillClick(o.id)}
                    style={{ background: '#16a34a22', border: '1px solid #16a34a66', color: '#4ade80',
                      borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                    Complete Wk {o.delivery_week}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ color: MUTED, fontSize: '13px' }}>Loading…</div>
          ) : orders.length === 0 ? (
            <div style={{ color: MUTED, fontSize: '13px', textAlign: 'center', padding: '48px 0' }}>
              {readOnly ? 'No orders found.' : 'No orders yet. Use "New Order" or "Excel Import" to add one.'}
            </div>
          ) : (
            sortedOrders.map(o => (
              <OrderCard key={o.id} order={o} startOpen={String(o.id) === linkedOrder} onSchedule={handleScheduleOrder} onDelete={handleDeleteOrder} onDeleteLine={handleDeleteLine} onEditLine={handleEditLine} onRename={handleRename} onEdit={!readOnly && isDirector() ? handleEditOrder : undefined} readOnly={readOnly} onFulfill={!readOnly ? () => handleFulfillClick(o.id) : undefined} onDispatch={handleDispatch} onLineDispatch={handleLineDispatch} onLineStock={!readOnly && isDirector() ? handleLineStock : undefined} canSeeHistory={!readOnly && isDirector()} invoices={invoicesByOrder[o.id]} onDismissNew={!readOnly && isDirector() ? handleDismissNew : undefined} onRunDone={!readOnly && isDirector() ? () => { loadOrders(); loadHistory(); } : undefined} />
            ))
          )}
          {!loading && historyOrders.length > 0 && (() => {
            // Last completed first; ten, then the rest on request.
            const done = [...historyOrders].sort((a, b) => String(b.fulfilled_at || '').localeCompare(String(a.fulfilled_at || '')));
            const shown = showAllCompleted ? done : done.slice(0, 10);
            return (
              <div style={{ marginTop: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ flex: 1, borderTop: `2px dashed ${BORDER}` }} />
                  <span style={{ fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Completed · {done.length}
                  </span>
                  <div style={{ flex: 1, borderTop: `2px dashed ${BORDER}` }} />
                </div>
                {shown.map(o => (
                  <OrderCard key={'done-' + o.id} order={o} completed readOnly startOpen={String(o.id) === linkedOrder}
                    onReopen={!readOnly && isDirector() ? () => handleReopen(o.id) : undefined}
                    canSeeHistory={!readOnly && isDirector()} invoices={invoicesByOrder[o.id]} />
                ))}
                {done.length > shown.length && (
                  <button onClick={() => setShowAllCompleted(true)}
                    style={{ width: '100%', background: 'none', border: `1px dashed ${BORDER}`, borderRadius: '8px', color: MUTED,
                      padding: '8px 0', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                    Show older ({done.length - shown.length})
                  </button>
                )}
              </div>
            );
          })()}
          {!readOnly && proposing && (
            <div style={{ color: MUTED, fontSize: '13px', marginTop: '12px' }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }} />
              Calculating schedule…
            </div>
          )}
        </div>
      )}

      {/* Manual form tab */}
      {tab === 'manual' && (
        <div style={{ maxWidth: '720px' }}>
          <ManualForm onSaved={handleManualSaved} />
        </div>
      )}

      {/* Excel tab */}
      {tab === 'excel' && (
        <div style={{ maxWidth: '720px' }}>
          <ExcelForm onSaved={handleExcelSaved} />
        </div>
      )}

      {/* Fulfill modal */}
      {showCapacity && (
        <CapacityCheckModal onClose={() => setShowCapacity(false)} />
      )}

      {fulfillModal && (
        <FulfillModal
          data={fulfillModal.data}
          confirming={fulfillConfirming}
          onConfirm={handleFulfillConfirm}
          onCancel={() => setFulfillModal(null)}
        />
      )}

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={handleEditOrderSaved}
        />
      )}

      {/* Conflict/proposal modal */}
      {scheduleState && scheduleState.pendingNaming && (
        <NamingScreen
          importedOrders={scheduleState.importedOrders || []}
          onContinue={handleNamingComplete}
          onSkip={() => setScheduleState(s => ({ ...s, pendingNaming: false }))}
          onCancelImport={handleCancelImport}
        />
      )}
      {scheduleState && !scheduleState.pendingNaming && (
        <ConflictModal
          proposed={scheduleState.proposalResult.proposed || []}
          conflicts={scheduleState.proposalResult.conflicts || []}
          warnings={scheduleState.proposalResult.warnings || []}
          floorCycles={scheduleState.proposalResult.floor_cycles || []}
          buildOrder={scheduleState.proposalResult.build_order || []}
          colourNotes={(scheduleState.proposalResult.colour && scheduleState.proposalResult.colour.notes) || []}
          batchOpportunities={scheduleState.pendingBatch ? (scheduleState.proposalResult.batch_opportunities || []) : []}
          onAccept={handleCommit}
          onReject={() => setScheduleState(null)}
          onRepropose={handleRepropose}
          onBatchDecide={handleBatchDecide}
          onCancelImport={scheduleState.importedOrderIds?.length ? handleCancelImport : undefined}
          onConfirmNoSchedule={scheduleState.importedOrderIds?.length ? handleConfirmNoSchedule : undefined}
        />
      )}
    </div>
  );
}
